import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Card, Row, Col, Table, Tabs, Segmented, Button, Drawer, Form, Input, InputNumber,
  Select, DatePicker, Space, Tag, Alert, Spin, Statistic, Tooltip, Empty, message, Modal,
} from 'antd';
import {
  PlusOutlined, ReloadOutlined, LockOutlined, UnlockOutlined, ThunderboltOutlined,
  CheckCircleFilled,
  ArrowUpOutlined, ArrowDownOutlined, WarningOutlined, SearchOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import PageLayout from '../components/layout/PageLayout';
import api from '../libs/api';

/**
 * Budget Control.
 *
 * Two audiences on one page. Everyone gets My Day — the suppliers they own and
 * the lines they raised. People whose remit spans an entity also get the entity
 * sub-pages and the access tab.
 *
 * Variance is a diverging quantity — over or under, with nil as the meaningful
 * middle — so it gets two hues and a neutral midpoint rather than a single ramp.
 * Utilisation is a magnitude and gets one hue. Neither is decided by the number
 * alone: every coloured figure carries its sign or a word beside it.
 */

const INK = '#141d26';
const INK_2 = '#5a6a7d';
const OVER = '#a32f2a';      // spent past budget
const UNDER = '#146c43';     // headroom left
const NEUTRAL = '#7c8ba0';   // no budget line to compare against
const BAR_BG = '#eef2f6';
const BAR = '#2a78d6';
const BAR_OVER = '#c4453f';

const fmtMoney = (v) => (v == null ? '—' : `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`);
const fmtSigned = (v) => (v == null ? '—'
  : `${v < 0 ? '−' : '+'}$${Math.abs(Number(v)).toLocaleString(undefined, { maximumFractionDigits: 0 })}`);

/** A thin utilisation bar. Over 100% it fills and turns, rather than overflowing. */
function UsageBar({ pct }) {
  if (pct == null) return <span style={{ color: NEUTRAL, fontSize: 11 }}>no budget</span>;
  const clamped = Math.min(pct, 100);
  const over = pct > 100;
  return (
    <Tooltip title={`${pct}% of budget used`}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 120 }}>
        <div style={{ flex: 1, height: 6, background: BAR_BG, borderRadius: 3, overflow: 'hidden' }}>
          <div style={{ width: `${clamped}%`, height: '100%', background: over ? BAR_OVER : BAR }} />
        </div>
        <span style={{
          fontVariantNumeric: 'tabular-nums', fontSize: 11,
          color: over ? OVER : INK_2, minWidth: 42, textAlign: 'right',
        }}
        >
          {pct}%
        </span>
      </div>
    </Tooltip>
  );
}

/** Month-on-month movement. The arrow carries the sign so colour never has to. */
function Movement({ value }) {
  if (!value) return <span style={{ color: NEUTRAL }}>—</span>;
  const up = value > 0;
  return (
    <span style={{ color: up ? OVER : UNDER, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
      {up ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
      {' '}
      {fmtMoney(Math.abs(value))}
    </span>
  );
}

export default function BudgetControl() {
  const [access, setAccess] = useState(null);
  // Controlled so a row in "Waiting on me" can take somebody to the bill it is
  // talking about. Keys match the two Tabs below.
  const [tab, setTab] = useState('mine');
  const [entityTab, setEntityTab] = useState('tracking');
  const [entity, setEntity] = useState(null);
  const [month, setMonth] = useState(dayjs().startOf('month'));
  const [overview, setOverview] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  // Pulling this month's submitted bills for every entity, so My day is current.
  const [queueSync, setQueueSync] = useState(null);   // { running, at, result }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [people, setPeople] = useState(null);
  const [savingRight, setSavingRight] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [unassigned, setUnassigned] = useState(null);
  const [assignDraft, setAssignDraft] = useState({});   // supplierId -> { ownerId, accountName }
  const [assigning, setAssigning] = useState(null);
  const [inbox, setInbox] = useState(null);
  const [inboxLoading, setInboxLoading] = useState(false);
  const [mineOnly, setMineOnly] = useState(false);
  const [deciding, setDeciding] = useState(null);
  const [approving, setApproving] = useState(null);   // { row, date }
  const [handing, setHanding] = useState(null);      // { row, toUserId }
  const [report, setReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  // Which dimensions the report groups by, in the order chosen, and the value
  // filters applied to each. Replaces a single "whose approvals" selector.
  const [reportGroupBy, setReportGroupBy] = useState(['supplier']);
  const [reportFilters, setReportFilters] = useState({
    entities: [], suppliers: [], accounts: [], people: [],
  });
  const [supRow, setSupRow] = useState(null);         // supplier open in the drawer
  const [supDraft, setSupDraft] = useState({});
  const [savingSup, setSavingSup] = useState(false);
  const [locking, setLocking] = useState(false);
  const [estimating, setEstimating] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [editAmount, setEditAmount] = useState(null);
  const [editAccount, setEditAccount] = useState({ code: '', name: '' });
  const [editNote, setEditNote] = useState('');
  const [savingLine, setSavingLine] = useState(false);
  // entity code -> its opex chart. The access tab lists suppliers from every
  // entity at once, so one chart for the selected entity is not enough: each
  // row has to offer the accounts of its own organisation.
  const [charts, setCharts] = useState({});
  const [people2, setPeople2] = useState([]);        // everyone, for handing a supplier on
  const [mySup, setMySup] = useState(null);          // one of my suppliers, open
  const [appointTo, setAppointTo] = useState(null);
  const [appointing, setAppointing] = useState(false);
  const [myLine, setMyLine] = useState(null);        // one of my budget lines, open
  const [myLineAmount, setMyLineAmount] = useState(null);
  const [myLineNote, setMyLineNote] = useState('');
  const [savingMyLine, setSavingMyLine] = useState(false);
  const [myLineMonth, setMyLineMonth] = useState(null);
  const [myLineFreq, setMyLineFreq] = useState(null);
  const [supBackup, setSupBackup] = useState(null);
  const [savingBackup, setSavingBackup] = useState(false);
  // One filter per column of the budget-line table, as a single object so the
  // reset is one call and adding a column is one key.
  const [lineFilter, setLineFilter] = useState({
    month: null, entity: null, origin: null, account: null, search: '',
  });
  const [supFilter, setSupFilter] = useState({ entity: null, role: null, search: '' });
  const [newOpen, setNewOpen] = useState(false);
  const [newLine, setNewLine] = useState({
    entity: null, supplierId: null, accountCode: '', accountName: '',
    amount: null, month: null, frequency: 'one_off', months: 1, note: '',
  });
  const [savingNew, setSavingNew] = useState(false);
  const [myLineAccount, setMyLineAccount] = useState({ code: '', name: '' });
  const chartsAsked = useRef(new Set());
  const [unlocking, setUnlocking] = useState(false);
  const [accountOwners, setAccountOwners] = useState(null);
  const [ruleDraft, setRuleDraft] = useState({
    entity: null, accountCode: '', accountName: '', ownerId: null, budgetBasis: 'supplier',
  });
  const [savingRule, setSavingRule] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const monthStr = month.format('YYYY-MM-01');

  /**
   * The entity's name as Xero holds it.
   *
   * The codes are how the tables are keyed and were never meant to be read:
   * "pw" and "gtl" are terse to the point of being cryptic beside a supplier's
   * full legal name. Falls back to the code for an entity the caller has no
   * access row for, which can happen where somebody owns a supplier in an
   * entity they cannot otherwise see.
   */
  const entityNameOf = useCallback(
    (code) => ((access?.entities || []).find((e) => e.code === code) || {}).name || code,
    [access],
  );
  const entityTag = (code) => <Tag>{entityNameOf(code)}</Tag>;
  const entityOptionsFrom = (codes) => codes.map((c) => ({ value: c, label: entityNameOf(c) }));

  useEffect(() => {
    api.get('/api/budget/access')
      .then((r) => {
        setAccess(r.data.data);
        const first = r.data.data.entities[0];
        if (first) setEntity(first.code);
      })
      .catch((err) => setError(err.response?.data?.message || 'Could not load your access'))
      .finally(() => setLoading(false));
    api.get('/api/budget/dashboard').then((r) => setDashboard(r.data.data)).catch(() => {});
  }, []);

  const loadDashboard = useCallback(async () => {
    try {
      const r = await api.get('/api/budget/dashboard');
      setDashboard(r.data.data);
    } catch { /* the page keeps what it had */ }
  }, []);

  /**
   * Pull today's submitted bills for every entity, then reload.
   *
   * A bill has no row here until something has read it out of Xero, so before
   * this My day could only show bills for entities somebody had already opened
   * the inbox on — the tab whose job is to say what is waiting was the one tab
   * that could not see a new bill.
   *
   * Deliberately not awaited by the dashboard load. The two run alongside each
   * other: the page paints from the database immediately, and the Xero pull —
   * which is several seconds per entity — replaces it when it lands. Waiting on
   * it would put a minute of Xero in front of a page somebody opens to glance
   * at.
   *
   * The ref guards re-entry: switching tabs away and back must not stack a
   * second pull on top of one still running.
   */
  const queuePull = useRef(false);
  const refreshQueues = useCallback(async ({ force = false } = {}) => {
    if (queuePull.current) return;
    queuePull.current = true;
    setQueueSync({ running: true });
    try {
      const r = await api.post('/api/budget/inbox/refresh', { force }, { timeout: 280000 });
      setQueueSync({ running: false, at: new Date(), result: r.data.data });
      // Only now is there anything new to show.
      await loadDashboard();
    } catch (err) {
      setQueueSync({
        running: false,
        at: new Date(),
        error: err.response?.data?.message || 'Could not check Xero for new bills',
      });
    }
    queuePull.current = false;
  }, [loadDashboard]);

  /**
   * Come back to My day and it is current.
   *
   * The dashboard was fetched once on mount and after a save, so a bill decided
   * on the Entity budgets tab, or by somebody else while the page sat open,
   * left this tab showing a queue that had moved on - and this is the tab that
   * claims to say what is waiting.
   *
   * Cheap enough to do on every return: it is one database read with no Xero
   * call behind it.
   */
  useEffect(() => {
    if (tab !== 'mine') return;
    loadDashboard();
    // Behind a five minute cooldown server-side, so returning to the tab
    // repeatedly costs one Xero pull rather than one per visit.
    refreshQueues();
  }, [tab, loadDashboard, refreshQueues]);

  // The month filter starts on the current month — the one somebody is working
  // on — and only until they touch it. Re-defaulting on every refresh would
  // undo their choice each time a save reloads the dashboard.
  const monthDefaulted = useRef(false);
  useEffect(() => {
    if (monthDefaulted.current || !dashboard?.month) return;
    monthDefaulted.current = true;
    if ((dashboard.myBudgetLines || []).some((l) => l.month === dashboard.month)) {
      setLineFilter((x) => ({ ...x, month: dashboard.month }));
    }
  }, [dashboard]);

  /**
   * One entity's budget and suppliers.
   *
   * Sequenced and abortable because the overview is slow - it reads a year of
   * bills out of Xero and can take the better part of a minute. Clicking from a
   * large entity to a small one landed the small one first and the large one
   * afterwards, so the page showed ShineHub's suppliers and ShineHub's totals
   * under Powow VPP's name, and the stale response also cleared the spinner.
   *
   * The last request to be *started* wins, not the last to arrive.
   */
  const entitySeq = useRef(0);
  const entityInflight = useRef(null);
  const loadEntity = useCallback(async () => {
    if (!entity) return;
    if (entityInflight.current) entityInflight.current.abort();
    const controller = new AbortController();
    entityInflight.current = controller;
    const seq = entitySeq.current + 1;
    entitySeq.current = seq;

    setLoading(true); setError(null);
    try {
      const [o, s2] = await Promise.all([
        api.get('/api/budget/overview', { params: { entity, month: monthStr }, timeout: 120000, signal: controller.signal }),
        api.get('/api/budget/suppliers', { params: { entity }, signal: controller.signal }),
      ]);
      if (seq !== entitySeq.current) return;
      setOverview(o.data.data);
      setSuppliers(s2.data.data);
    } catch (err) {
      // An aborted request is this function superseding itself, not a failure.
      if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') return;
      if (seq !== entitySeq.current) return;
      setError(err.response?.data?.message || 'Could not load this entity');
      setOverview(null);
    } finally {
      if (seq === entitySeq.current) setLoading(false);
    }
  }, [entity, monthStr]);

  /**
   * Fetch one entity's chart, once.
   *
   * Typing a code was how this worked, and it asked people to remember five
   * digits and a category's exact wording — a code typed from memory is a budget
   * line quietly attached to the wrong account, or to none.
   *
   * The asked-set is a ref rather than state so this callback keeps a stable
   * identity: reading the cache it writes to would make it a new function on
   * every load and re-trigger the effects that depend on it.
   */
  const ensureAccounts = useCallback(async (ent) => {
    if (!ent || chartsAsked.current.has(ent)) return;
    chartsAsked.current.add(ent);
    try {
      const r = await api.get('/api/budget/accounts', { params: { entity: ent }, timeout: 60000 });
      setCharts((m) => ({ ...m, [ent]: r.data.data }));
    } catch {
      // The picker keeps whatever the row already had rather than blocking on
      // the chart, and says so.
      setCharts((m) => ({ ...m, [ent]: [] }));
    }
  }, []);

  /**
   * Options per entity, searchable on code and name together, because people
   * arrive knowing one or the other.
   */
  const accountOptions = useMemo(() => {
    const out = {};
    for (const [ent, list] of Object.entries(charts)) {
      out[ent] = (list || []).map((a) => ({
        value: a.code,
        label: `${a.code} · ${a.name}`,
        name: a.name,
      }));
    }
    return out;
  }, [charts]);

  /** The same options keyed by name, for the fields that store a name. */
  const accountNameOptions = useMemo(() => {
    const out = {};
    for (const [ent, list] of Object.entries(charts)) {
      const seen = new Set();
      out[ent] = (list || []).filter((a) => {
        if (seen.has(a.name)) return false;
        seen.add(a.name);
        return true;
      }).map((a) => ({ value: a.name, label: `${a.code} · ${a.name}` }));
    }
    return out;
  }, [charts]);

  useEffect(() => { loadEntity(); }, [loadEntity]);

  /**
   * The bill inbox is loaded on request, not with the entity — it costs a Xero
   * round trip — so its rows outlived a change of entity and the tab went on
   * showing the previous one's bills under the new one's name.
   *
   * Cleared rather than reloaded. Reloading would fire an expensive fetch every
   * time somebody clicked through the entity tabs to look at budgets, and the
   * load button is one click. Showing the wrong entity's bills beside an approve
   * button that authorises payment is the part that had to stop.
   */
  // Everything on this tab belongs to one entity-month, so none of it may
  // outlive a change of either. Blanked rather than left under a spinner: the
  // old figures read as this entity's until the new ones arrive.
  useEffect(() => { setInbox(null); setOverview(null); setSuppliers([]); }, [entity, monthStr]);

  /**
   * A deletion that has to be typed out.
   *
   * Used where the thing being removed cannot be reconstructed from the screen —
   * a supplier's ownership and coding, a budget figure somebody agreed. A click
   * is too cheap for that: the word is the pause.
   */
  const confirmByTyping = ({ title, body, onConfirm }) => {
    let typed = '';
    const modal = Modal.confirm({
      title,
      icon: <WarningOutlined style={{ color: OVER }} />,
      okText: 'OK',
      okButtonProps: { danger: true, disabled: true },
      content: (
        <div>
          <div style={{ fontSize: 13, marginBottom: 10 }}>{body}</div>
          <div style={{ fontSize: 12, color: INK_2, marginBottom: 4 }}>
            Type <b>delete</b> to confirm
          </div>
          <Input
            autoFocus
            placeholder="delete"
            onChange={(e) => {
              typed = e.target.value;
              modal.update({ okButtonProps: { danger: true, disabled: typed.trim().toLowerCase() !== 'delete' } });
            }}
            onPressEnter={() => {
              if (typed.trim().toLowerCase() === 'delete') { modal.destroy(); onConfirm(); }
            }}
          />
        </div>
      ),
      onOk: () => {
        if (typed.trim().toLowerCase() !== 'delete') return Promise.reject(new Error('not confirmed'));
        return onConfirm();
      },
    });
    return modal;
  };

  /**
   * An account picker for one entity's chart.
   *
   * byName is for the fields that store the account's name rather than its code;
   * either way the list shows and filters on both, so "6140" and "bank" each
   * find Bank Fee. The chart is fetched when the list is first opened rather
   * than up front — the access tab would otherwise pull five charts to render a
   * table nobody may touch.
   */
  const accountPicker = (ent, value, onPick, { disabled = false, byName = false, size } = {}) => {
    const options = (byName ? accountNameOptions[ent] : accountOptions[ent]) || [];
    const asked = charts[ent] !== undefined;
    return (
      <Select
        showSearch
        allowClear
        size={size}
        style={{ width: '100%' }}
        placeholder={ent ? 'Search by code or name' : 'Choose an entity first'}
        disabled={disabled || !ent}
        value={value || undefined}
        options={options}
        onDropdownVisibleChange={(open) => { if (open) ensureAccounts(ent); }}
        filterOption={(input, option) => option.label.toLowerCase().includes(input.toLowerCase())}
        notFoundContent={!asked ? 'Loading the chart…'
          : (options.length ? 'No matching opex account' : 'Chart of accounts unavailable')}
        onChange={(picked) => {
          if (byName) { onPick(picked || null, picked || null); return; }
          const hit = options.find((o) => o.value === picked);
          onPick(picked || null, hit ? hit.name : null);
        }}
      />
    );
  };

  // Everyone, so a supplier can be handed on without the access right. Loaded
  // once: it is a short list and it does not change while the page is open.
  const loadEveryone = useCallback(async () => {
    try {
      const r = await api.get('/api/budget/users');
      setPeople2(r.data.data);
    } catch { /* the picker shows empty rather than blocking the drawer */ }
  }, []);

  useEffect(() => { ensureAccounts(entity); }, [entity, ensureAccounts]);
  useEffect(() => { loadEveryone(); }, [loadEveryone]);

  const loadPeople = useCallback(async () => {
    try {
      const r = await api.get('/api/budget/people');
      setPeople(r.data.data);
    } catch (err) {
      message.error(err.response?.data?.message || 'Could not load people');
    }
  }, []);

  const loadAccountOwners = useCallback(async () => {
    try {
      const r = await api.get('/api/budget/account-policy');
      setAccountOwners(r.data.data);
    } catch { /* the panel shows empty rather than shouting */ }
  }, []);

  const loadUnassigned = useCallback(async () => {
    try {
      const r = await api.get('/api/budget/suppliers/unassigned');
      setUnassigned(r.data.data);
    } catch (err) {
      message.error(err.response?.data?.message || 'Could not load unassigned suppliers');
    }
  }, []);

  useEffect(() => {
    if (!access?.canManageAccess) return;
    loadPeople();
    loadUnassigned();
    loadAccountOwners();
  }, [access, loadPeople, loadUnassigned, loadAccountOwners]);

  /**
   * Owner and default account are saved together. Whoever knows who owns a supplier
   * is the person who knows how it should be coded, and splitting them across two
   * screens is how one of them stays unset.
   */
  const assignSupplier = async (row) => {
    const draft = assignDraft[row.id] || {};
    const ownerId = draft.ownerId;
    if (!ownerId) { message.warning('Choose an owner'); return; }
    setAssigning(row.id);
    try {
      await api.put(`/api/budget/suppliers/${row.id}/owner`, {
        ownerId,
        accountName: draft.accountName !== undefined ? draft.accountName : row.learnedCostCentre,
      });
      setAssignDraft((d) => { const n = { ...d }; delete n[row.id]; return n; });
      await Promise.all([loadUnassigned(), loadPeople()]);
      if (entity === row.entity) loadEntity();
    } catch (err) {
      message.error(err.response?.data?.message || 'Could not assign');
    }
    setAssigning(null);
  };

  /**
   * Toggling one right writes the whole trio for that person and entity, because
   * the endpoint takes a state rather than a delta — and a state cannot be
   * half-applied if two people edit at once.
   */
  const toggleRight = async (person, entityCode, key) => {
    const cur = person.rights[entityCode] || { canApply: false, canApproveOverspend: false, canLock: false };
    const next = { ...cur, [key]: !cur[key] };
    const tag = `${person.id}:${entityCode}:${key}`;
    setSavingRight(tag);
    try {
      await api.put(`/api/budget/people/${person.id}/access`, { entity: entityCode, ...next });
      await loadPeople();
    } catch (err) {
      message.error(err.response?.data?.message || 'Could not change access');
    }
    setSavingRight(null);
  };

  const current = useMemo(
    () => (access?.entities || []).find((e) => e.code === entity) || null,
    [access, entity],
  );

  const submitDrawer = async () => {
    try {
      const v = await form.validateFields();
      setSaving(true);
      const payload = {
        entity,
        dimension: v.dimension,
        amount: v.amount,
        month: v.month.format('YYYY-MM-01'),
        frequency: v.frequency,
        months: v.months,
        accountCode: v.accountCode || null,
        accountName: v.accountName || null,
        note: v.note || null,
      };
      if (v.dimension === 'supplier') {
        const s = suppliers.find((x) => x.id === v.supplierId);
        payload.refKey = s?.xeroContactId;
        payload.refName = s?.name;
      } else {
        payload.refKey = v.accountCode;
        payload.refName = v.accountName || v.accountCode;
      }
      const r = await api.post('/api/budget/budgets', payload);
      message.success(`${r.data.data.created.length} month(s) recorded`);
      setDrawerOpen(false);
      form.resetFields();
      loadEntity();
    } catch (err) {
      if (err?.errorFields) return;            // form validation, already shown
      message.error(err.response?.data?.message || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  /**
   * Locking is one-way. Saying so at the point of the click matters more than in
   * a document nobody rereads.
   */
  const lockMonth = async (confirmEmpty = false) => {
    setLocking(true);
    try {
      const r = await api.post('/api/budget/periods/lock', { entity, month: monthStr, confirmEmpty });
      message.success(`${dayjs(monthStr).format('MMM YYYY')} locked — ${r.data.data.lines} lines, ${fmtMoney(r.data.data.total)}`);
      loadEntity();
    } catch (err) {
      const body = err.response?.data;
      if (body?.code === 'EMPTY_MONTH') {
        Modal.confirm({
          title: 'Lock a month with no budget?',
          content: body.message,
          okText: 'Lock it anyway',
          okButtonProps: { danger: true },
          onOk: () => lockMonth(true),
        });
      } else {
        message.error(body?.message || 'Could not lock the month');
      }
    }
    setLocking(false);
  };

  /**
   * Replace the month's working budget with one read off the last three months.
   *
   * Confirmed before anything is read, not after: the estimate itself takes
   * three months of Xero paging, and asking first keeps the button honest about
   * what it is going to do.
   */
  const runEstimate = () => Modal.confirm({
    title: `Confirm to erase the current budget for ${current?.name || entity}, ${month.format('MMM-YYYY')}?`,
    content: (
      <span style={{ fontSize: 13 }}>
        Previously estimated lines for this month are replaced by a fresh estimate built from the
        three months to {dayjs(monthStr).subtract(1, 'month').format('MMM YYYY')} — recurring
        supplier-account pairs and envelope accounts, averaged over the months they actually
        appeared in.
        <div style={{ marginTop: 8 }}>
          <b>Hand-entered lines are left alone.</b> A cost somebody raised for this month is a
          commitment they know about and history does not, so it is neither replaced nor
          duplicated. Retired lines stay in the history; nothing is deleted.
        </div>
      </span>
    ),
    okText: 'Erase and estimate',
    okButtonProps: { danger: true },
    onOk: async () => {
      setEstimating(true);
      try {
        const r = await api.post('/api/budget/budgets/estimate', { entity, month: monthStr }, { timeout: 300000 });
        const d = r.data.data;
        message.success(
          `${d.created} lines totalling ${fmtMoney(d.total)}`
          + (d.replaced ? `, replacing ${d.replaced} estimated` : '')
          + (d.keptManual ? `, keeping ${d.keptManual} hand-entered` : '')
          + (d.skipped ? `. ${d.skipped} non-recurring lines were left out.` : ''),
          8,
        );
        if ((d.raisedFor || []).length) {
          // The lines are not the presser's — say whose they are, or somebody
          // finds a list they did not make and nobody knows who owns it.
          message.info(
            `Raised for ${d.raisedFor.map((x) => `${x.name} (${x.lines})`).join(', ')}`,
            10,
          );
        }
        if (!d.created) {
          message.info(
            'Nothing recurred in at least two of the last three months, so there was nothing to estimate.',
            8,
          );
        }
        loadEntity();
      } catch (err) {
        message.error(err.response?.data?.message || 'Could not build the estimate');
      }
      setEstimating(false);
    },
  });

  const unlockAction = async (path, body, done) => {
    setUnlocking(true);
    try {
      const r = await api.post(`/api/budget/periods/${path}`, { entity, month: monthStr, ...body });
      done(r.data.data);
      loadEntity();
    } catch (err) {
      message.error(err.response?.data?.message || 'Could not do that');
    }
    setUnlocking(false);
  };

  const requestUnlock = () => {
    // eslint-disable-next-line no-alert
    const reason = window.prompt(
      `Why does ${month.format('MMMM YYYY')} need reopening? This is recorded permanently.`,
    );
    if (reason === null) return;
    if (!reason.trim()) { message.warning('A reason is required'); return; }
    unlockAction('unlock-request', { reason }, (d) => message.success(
      `Requested — ${d.signatures} of ${d.required} signatures. One more person who can lock this entity has to agree.`,
      8,
    ));
  };

  const totals = overview?.totals;
  const locked = overview?.period?.state === 'locked';
  const unlock = overview?.period?.unlock || null;

  /**
   * Clicking a row opens it for editing. A row with no budget line has nothing
   * to edit, so it opens the create form pre-filled instead — that row already
   * knows the supplier and the account its bills land in, and making somebody
   * retype both would be the wrong answer to a click.
   */
  const openRow = (row, dimension) => {
    if (locked || !current?.canApply) return;
    if (!row.budgetId) {
      const sup = dimension === 'supplier'
        ? suppliers.find((x) => x.xeroContactId === row.key)
        : null;
      form.resetFields();
      form.setFieldsValue({
        dimension,
        month,
        frequency: 'one_off',
        months: 1,
        supplierId: sup ? sup.id : undefined,
        accountCode: dimension === 'account' ? row.key : (row.mainAccount || undefined),
        accountName: row.accountName || undefined,
      });
      setDrawerOpen(true);
      return;
    }
    setEditRow({ ...row, dimension });
    setEditAmount(row.budget);
    setEditAccount({ code: row.accountCode || '', name: row.accountName || '' });
    setEditNote(row.note || '');
  };

  const saveLine = async () => {
    if (!(Number(editAmount) > 0)) { message.warning('Amount must be greater than zero'); return; }
    setSavingLine(true);
    try {
      const r = await api.put(`/api/budget/budgets/${editRow.budgetId}`, {
        amount: Number(editAmount),
        accountCode: editAccount.code || null,
        accountName: editAccount.name || null,
        note: editNote || null,
      });
      const d = r.data.data;
      message.success(`${editRow.name}: ${fmtMoney(d.was)} → ${fmtMoney(d.amount)}`);
      setEditRow(null);
      loadEntity();
    } catch (err) {
      message.error(err.response?.data?.message || 'Could not save the change');
    }
    setSavingLine(false);
  };

  const removeLine = () => confirmByTyping({
    title: `Remove the budget for ${editRow.name}?`,
    body: `${fmtMoney(editRow.budget)} for ${month.format('MMMM YYYY')} stops being what this line's bills are checked against. The figure stays in the history.`,
    onConfirm: async () => {
      setSavingLine(true);
      try {
        await api.delete(`/api/budget/budgets/${editRow.budgetId}`);
        message.success(`Budget for ${editRow.name} removed`);
        setEditRow(null);
        loadEntity();
      } catch (err) {
        message.error(err.response?.data?.message || 'Could not remove the line');
      }
      setSavingLine(false);
    },
  });

  const loadReport = useCallback(async () => {
    // No entity guard any more: the report spans every entity the caller can
    // see, and which ones are included is a filter rather than the page's
    // current selection.
    setReportLoading(true);
    try {
      const r = await api.get('/api/budget/report', {
        params: {
          month: monthStr,
          groupBy: reportGroupBy.join(','),
          // Empty means "no filter"; the server reads an absent parameter that
          // way, so nothing is sent for a picker nobody has touched.
          ...(reportFilters.entities.length ? { entities: reportFilters.entities.join(',') } : {}),
          ...(reportFilters.suppliers.length ? { suppliers: reportFilters.suppliers.join(',') } : {}),
          ...(reportFilters.accounts.length ? { accounts: reportFilters.accounts.join(',') } : {}),
          ...(reportFilters.people.length ? { userIds: reportFilters.people.join(',') } : {}),
        },
        timeout: 180000,
      });
      setReport(r.data.data);
    } catch (err) {
      message.error(err.response?.data?.message || 'Could not build the report');
      setReport(null);
    }
    setReportLoading(false);
  }, [monthStr, reportGroupBy, reportFilters]);

  const everyoneOptions = useMemo(() => people2.map((p) => ({
    value: p.id,
    // Email in the label because two people can share a name, and handing a
    // supplier to the wrong one hands over its payments.
    label: p.email && p.email !== p.name ? `${p.name} · ${p.email}` : p.name,
  })), [people2]);

  const appoint = async () => {
    if (!appointTo) { message.warning('Choose who to hand it to'); return; }
    setAppointing(true);
    try {
      const r = await api.put(`/api/budget/suppliers/${mySup.id}/appoint`, { toUserId: appointTo });
      const d = r.data.data;
      message.success(
        `${d.name} is now ${d.ownerName}’s`
        + (d.clearedBackup ? ' — they were the backup, so the backup is now unset' : ''),
        6,
      );
      setMySup(null);
      setAppointTo(null);
      loadDashboard();
    } catch (err) {
      message.error(err.response?.data?.message || 'Could not hand the supplier on');
    }
    setAppointing(false);
  };

  /** Months this entity has already signed off, so the picker can refuse them. */
  const lockedFor = (ent) => new Set((dashboard?.lockedMonths || {})[ent] || []);
  const isLockedMonth = (ent, m) => !!m && lockedFor(ent).has(m.startOf('month').format('YYYY-MM-01'));

  const submitNewLine = async () => {
    const f = newLine;
    if (!f.entity || !f.supplierId || !(Number(f.amount) > 0) || !f.month) {
      message.warning('Entity, supplier, amount and month are all needed');
      return;
    }
    if (isLockedMonth(f.entity, f.month)) {
      message.error(`${f.month.format('MMMM YYYY')} is locked, so nothing can be added to it`);
      return;
    }
    const sup = (dashboard?.suppliers || []).find((x) => x.id === f.supplierId);
    setSavingNew(true);
    try {
      const r = await api.post('/api/budget/budgets', {
        entity: f.entity,
        dimension: 'supplier',
        refKey: sup.xeroContactId,
        refName: sup.name,
        accountCode: f.accountCode || null,
        accountName: f.accountName || null,
        amount: Number(f.amount),
        month: f.month.format('YYYY-MM-01'),
        frequency: f.frequency,
        months: f.months,
        note: f.note || null,
      });
      const n = r.data.data.created.length;
      message.success(`${n} budget line${n === 1 ? '' : 's'} raised for ${sup.name}`);
      setNewOpen(false);
      loadDashboard();
    } catch (err) {
      message.error(err.response?.data?.message || 'Could not raise the budget line');
    }
    setSavingNew(false);
  };

  const saveBackup = async () => {
    setSavingBackup(true);
    try {
      await api.put(`/api/budget/suppliers/${mySup.id}/backup`, { backupId: supBackup || null });
      message.success(supBackup ? 'Backup approver set' : 'Backup approver cleared');
      setMySup(null);
      loadDashboard();
    } catch (err) {
      message.error(err.response?.data?.message || 'Could not set the backup');
    }
    setSavingBackup(false);
  };

  const saveMyLine = async () => {
    if (!(Number(myLineAmount) > 0)) { message.warning('Amount must be greater than zero'); return; }
    setSavingMyLine(true);
    try {
      const r = await api.put(`/api/budget/budgets/${myLine.id}`, {
        amount: Number(myLineAmount),
        note: myLineNote || null,
        month: myLineMonth ? myLineMonth.format('YYYY-MM-01') : undefined,
        frequency: myLineFreq || undefined,
        accountCode: myLineAccount.code || null,
        accountName: myLineAccount.name || null,
      });
      message.success(`${myLine.name}: ${fmtMoney(r.data.data.was)} → ${fmtMoney(r.data.data.amount)}`);
      setMyLine(null);
      loadDashboard();
      if (myLine.entity === entity) loadEntity();
    } catch (err) {
      message.error(err.response?.data?.message || 'Could not save the change');
    }
    setSavingMyLine(false);
  };

  const loadInbox = useCallback(async () => {
    if (!entity) return;
    setInboxLoading(true);
    try {
      const r = await api.get('/api/budget/inbox', { params: { entity, month: monthStr }, timeout: 180000 });
      setInbox(r.data.data);
    } catch (err) {
      message.error(err.response?.data?.message || 'Could not load the inbox');
      setInbox(null);
    }
    setInboxLoading(false);
  }, [entity, monthStr]);

  /**
   * Load the queue when its tab is the one being looked at.
   *
   * The inbox was loaded only by its own button, so it did not follow a change
   * of entity - the previous entity's bills sat under the new entity's name.
   * Clearing them fixed the lie but left the tab needing a manual press, and a
   * row in "Waiting on me" could not send anybody to the bill it named.
   *
   * Only while this tab is open, and only when there is nothing loaded, so the
   * Xero round trip does not fire for somebody clicking through entities to
   * look at budgets.
   */
  useEffect(() => {
    if (tab !== 'entity' || entityTab !== 'bills') return;
    if (inbox || inboxLoading || !entity) return;
    loadInbox();
  }, [tab, entityTab, inbox, inboxLoading, entity, loadInbox]);

  const decide = async (row, decision, extra = {}) => {
    let comment = null;
    if (decision === 'rejected') {
      // eslint-disable-next-line no-alert
      comment = window.prompt(`Why is ${row.supplierName} ${fmtMoney(row.opexAmount)} being rejected?`);
      if (comment === null) return;                 // cancelled, not a silent no-op
      if (!comment.trim()) { message.warning('A rejection needs a reason'); return; }
    }
    setDeciding(row.reviewId);
    try {
      const r = await api.post(`/api/budget/inbox/${row.reviewId}/decision`, { decision, comment, ...extra });
      const d = r.data.data;
      const when = d.plannedPaymentDate ? ` · payment planned ${dayjs(d.plannedPaymentDate).format('D MMM')}` : '';
      if (d.escalated) {
        message.success(`Approved and sent to the executive — this bill is over budget${when}`, 6);
      } else if (decision === 'reassigned') {
        message.success('Handed on — it is now in their queue', 5);
      } else if (d.outcome === 'approved' && d.xero && d.xero.plannedPaymentSet === false) {
        // The authorisation went through and the date did not. Both halves are
        // said, because the payment is now released and the schedule is not.
        message.warning(
          `Approved and authorised in Xero, but Xero would not take the planned payment date${
            d.plannedPaymentDate ? ` of ${dayjs(d.plannedPaymentDate).format('D MMM')}` : ''
          } — set it on the bill in Xero`,
          9,
        );
      } else if (d.outcome === 'approved') {
        // Say that Xero moved, because that is the part that makes the payment
        // possible and the part somebody would otherwise go and check.
        message.success(
          d.xero?.already
            ? `Recorded — Xero already had it as ${String(d.xero.status).toLowerCase()}${when}`
            : `${d.signedAsExecutive ? 'Approved as the over-run approver — no second signature needed'
              : 'Approved'} and authorised in Xero${when}`,
          6,
        );
      } else {
        message.success(`Recorded as ${d.outcome || decision}${when}`);
      }
      setApproving(null);
      setHanding(null);
      loadInbox();
    } catch (err) {
      message.error(err.response?.data?.message || 'Could not record the decision');
    }
    setDeciding(null);
  };

  // ---------------------------------------------------------------- columns
  const trackingColumns = (label) => [
    {
      title: label, dataIndex: 'name', fixed: 'left', width: 240,
      render: (v, r) => (
        <div>
          <div style={{ color: INK }}>
            {v}
            {r.origin === 'manual' && (
              <Tooltip title="Entered by hand. The estimate will not replace or duplicate it.">
                <Tag color="cyan" style={{ marginLeft: 6 }}>manual</Tag>
              </Tooltip>
            )}
            {r.basis === 'account' && (
              <Tooltip title="Budgeted as a monthly envelope. Any payee draws it down, so its bills need no per-supplier line.">
                <Tag color="geekblue" style={{ marginLeft: 6 }}>envelope</Tag>
              </Tooltip>
            )}
          </div>
          {r.accountName && (
            <div style={{ fontSize: 11, color: NEUTRAL }}>{r.accountCode} · {r.accountName}</div>
          )}
        </div>
      ),
    },
    {
      title: 'Budget', dataIndex: 'budget', width: 110, align: 'right',
      sorter: (a, b) => (a.budget || 0) - (b.budget || 0),
      render: (v, r) => {
        if (v != null && r.budgetSource === 'rolled_up') {
          return (
            <Tooltip title={`Added up from ${r.rolledUpLines} supplier ${r.rolledUpLines === 1 ? 'line' : 'lines'} coded to this account. Click the row to cap the category below their sum.`}>
              <div>
                <div style={{ fontVariantNumeric: 'tabular-nums', color: INK_2 }}>{fmtMoney(v)}</div>
                <div style={{ fontSize: 11, color: NEUTRAL }}>
                  from {r.rolledUpLines} supplier{r.rolledUpLines === 1 ? '' : 's'}
                </div>
              </div>
            </Tooltip>
          );
        }
        if (v != null && r.budgetSource === 'expected') {
          const over = r.rolledUp != null && r.rolledUp > v;
          return (
            <Tooltip title={
              'What this category is expected to cost. The supplier lines inside it are the '
              + 'commitment and are what the entity total counts, so this figure is a reference '
              + 'rather than additional budget.'
              + (r.rolledUp != null ? ` Those lines add up to ${fmtMoney(r.rolledUp)}.` : '')
            }
            >
              <div>
                <div style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(v)}</div>
                <div style={{ fontSize: 11, color: over ? OVER : NEUTRAL }}>
                  {r.rolledUp != null
                    ? `expected · suppliers ${fmtMoney(r.rolledUp)}`
                    : 'expected · no supplier lines'}
                </div>
              </div>
            </Tooltip>
          );
        }
        if (v != null) return <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(v)}</span>;
        // A vendor billed against an envelope account is not a gap in the
        // budget — the envelope is the budget, and flagging each airline orange
        // would bury the rows that genuinely need a line.
        if (r.coveredByEnvelope) {
          return (
            <Tooltip title="Covered by a monthly envelope on the account it codes to, so no line for this supplier is expected">
              <Tag color="geekblue" style={{ margin: 0 }}>envelope</Tag>
            </Tooltip>
          );
        }
        return (
          <Tooltip title="Spent with no budget line for this month">
            <Tag color="orange" style={{ margin: 0 }}>none</Tag>
          </Tooltip>
        );
      },
    },
    {
      title: 'Actual', dataIndex: 'actual', width: 110, align: 'right',
      defaultSortOrder: 'descend',
      sorter: (a, b) => a.actual - b.actual,
      render: (v) => <span style={{ fontVariantNumeric: 'tabular-nums', color: INK }}>{fmtMoney(v)}</span>,
    },
    {
      title: 'Difference', dataIndex: 'variance', width: 130, align: 'right',
      sorter: (a, b) => (a.variance ?? 0) - (b.variance ?? 0),
      render: (v) => {
        if (v == null) return <span style={{ color: NEUTRAL }}>—</span>;
        const over = v < 0;
        return (
          <Tooltip title={over ? 'Over budget' : 'Under budget'}>
            <span style={{ color: over ? OVER : UNDER, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
              {over && <WarningOutlined style={{ marginRight: 4 }} />}
              {fmtSigned(v)}
            </span>
          </Tooltip>
        );
      },
    },
    { title: 'Used', dataIndex: 'utilisation', width: 170, render: (v) => <UsageBar pct={v} /> },
    {
      title: 'vs last month', dataIndex: 'movement', width: 130, align: 'right',
      sorter: (a, b) => a.movement - b.movement,
      render: (v) => <Movement value={v} />,
    },
    {
      title: 'Last month', dataIndex: 'prevActual', width: 110, align: 'right',
      render: (v) => <span style={{ fontVariantNumeric: 'tabular-nums', color: INK_2 }}>{fmtMoney(v)}</span>,
    },
  ];

  // ---------------------------------------------------------------- panels

  const entityHeader = (
    <Card size="small" style={{ marginBottom: 12 }} styles={{ body: { padding: '10px 14px' } }}>
      <Space wrap size="middle" style={{ width: '100%', justifyContent: 'space-between' }}>
        <Space wrap size="middle">
          <Segmented
            size="small"
            value={entity}
            onChange={setEntity}
            options={(access?.entities || []).map((e) => ({ label: e.name, value: e.code }))}
          />
          <DatePicker
            size="small" picker="month" value={month} allowClear={false}
            onChange={(v) => v && setMonth(v.startOf('month'))}
          />
          {overview && (
            <Tag
              icon={locked ? <LockOutlined /> : <UnlockOutlined />}
              color={locked ? 'blue' : 'default'}
              style={{ margin: 0 }}
            >
              {locked ? `locked ${dayjs(overview.period.locked_at).format('D MMM')}` : 'open'}
            </Tag>
          )}
        </Space>
        <Space size="small">
          {current?.canApply && (
            <Button
              size="small" type="primary" icon={<PlusOutlined />}
              style={{ background: '#54A2CC', borderColor: '#54A2CC' }}
              onClick={() => { form.resetFields(); setDrawerOpen(true); }}
              disabled={locked}
            >
              Expected cost
            </Button>
          )}
          {current?.canLock && !locked && (
            <Tooltip title="Discard this month's budget and rebuild it from the last three months of bills — recurring lines only. Finance manager and executive only.">
              <Button
                size="small" icon={<ThunderboltOutlined />} loading={estimating}
                onClick={runEstimate}
              >
                estimate
              </Button>
            </Tooltip>
          )}
          {current?.canLock && !locked && (
            <Tooltip title="Signs this month off and freezes it. Bills are then checked against these figures — and it cannot be unlocked, because a budget that can be edited afterwards cannot say what was planned.">
              <Button
                size="small" icon={<LockOutlined />} loading={locking}
                onClick={() => Modal.confirm({
                  title: `Lock ${dayjs(monthStr).format('MMMM YYYY')} for ${current.name}?`,
                  content: totals?.budgetLines
                    ? `${totals.budgetLines} budget lines totalling ${fmtMoney(totals.budget)} become the figures every bill is checked against. This cannot be undone.`
                    : 'This cannot be undone.',
                  okText: 'Lock',
                  onOk: () => lockMonth(false),
                })}
              >
                lock month
              </Button>
            </Tooltip>
          )}
          {current?.canLock && locked && !unlock && (
            <Tooltip title="Reopening a locked month takes two of the people who can lock this entity, and the reason is recorded permanently.">
              <Button size="small" icon={<UnlockOutlined />} loading={unlocking} onClick={requestUnlock}>
                request unlock
              </Button>
            </Tooltip>
          )}
          <Button
            size="small" icon={<ReloadOutlined />} loading={loading || inboxLoading}
            /**
             * Refreshes whichever tab is open.
             *
             * The bill inbox had a refresh of its own beside this one, which is
             * two buttons for the same intention - and after the inbox began
             * loading with its tab, the second one had nothing left to do that
             * this one could not.
             */
            onClick={() => { loadEntity(); if (entityTab === 'bills') loadInbox(); }}
          >
            refresh
          </Button>
        </Space>
      </Space>
    </Card>
  );

  /**
   * An open unlock request, shown to everyone who can see the entity rather than
   * only to the people who can sign it. A month being reopened is information
   * the people reading its figures need.
   */
  const unlockBanner = unlock && (
    <Alert
      type="warning"
      showIcon
      style={{ marginBottom: 12 }}
      message={(
        <span>
          Unlock requested by <b>{unlock.requestedByName || 'someone'}</b>
          {' — '}
          <b>{unlock.signatures} of {unlock.required} signatures</b>
          {unlock.signatures < unlock.required && ' · needs one more person who can lock this entity'}
        </span>
      )}
      description={(
        <span style={{ fontSize: 12 }}>
          &ldquo;{unlock.reason}&rdquo;
          {' · asked '}{dayjs(unlock.requestedAt).format('D MMM HH:mm')}
        </span>
      )}
      action={current?.canLock && (
        <Space direction="vertical" size={4}>
          <Button
            size="small" type="primary" loading={unlocking}
            disabled={unlock.isMine}
            style={unlock.isMine ? undefined : { background: '#54A2CC', borderColor: '#54A2CC' }}
            onClick={() => unlockAction('unlock-approve', {}, (d) => message.success(
              d.state === 'open'
                ? `${month.format('MMMM YYYY')} reopened on ${d.signatures} signatures`
                : `Signed — ${d.signatures} of ${d.required}`,
            ))}
          >
            {unlock.isMine ? 'you asked' : 'approve unlock'}
          </Button>
          <Button
            size="small" loading={unlocking}
            onClick={() => unlockAction('unlock-cancel', {}, () => message.success('Request withdrawn'))}
          >
            withdraw
          </Button>
        </Space>
      )}
    />
  );

  // The total will not match the bill total in Xero, and somebody will check.
  // Saying why, with the figure, is cheaper than fielding the question.
  const exclusionNote = totals?.excluded?.amount > 0 && (
    <Alert
      type="info"
      style={{ marginBottom: 12 }}
      message={(
        <span style={{ fontSize: 12 }}>
          Opex only — <b>{fmtMoney(totals.excluded.amount)}</b> of this month&apos;s bills is excluded
          {totals.excluded.directCosts > 0 && <> ({fmtMoney(totals.excluded.directCosts)} cost of sales</>}
          {totals.excluded.otherClasses > 0 && <>, {fmtMoney(totals.excluded.otherClasses)} balance-sheet accounts</>}
          {totals.excluded.directCosts > 0 && <>)</>}
          . {totals.billsSeen} bills read, {totals.bills} carried opex.
          {totals.excluded.unclassifiedLines > 0 && (
            <> {totals.excluded.unclassifiedLines} lines had no account code and could not be classified.</>
          )}
        </span>
      )}
    />
  );

  /**
   * Four cards, each carrying its own movement rather than one card carrying it
   * for the whole page.
   *
   * "vs Aug" as a separate card could only be about one of the figures, and it
   * was about actuals — so the Budget card sat beside a comparison that was not
   * its own. Both now say how they moved, which is the question anybody asks of
   * either: budget up and actual flat is a different month from both rising.
   */
  const movementNote = (now, before, { higherIsWorse }) => {
    if (before == null) {
      return <span style={{ color: NEUTRAL }}>no figure last month</span>;
    }
    const delta = Number(((now || 0) - before).toFixed(2));
    if (Math.abs(delta) < 1) {
      return <span style={{ color: NEUTRAL }}>level with {fmtMoney(before)} last month</span>;
    }
    const bad = higherIsWorse ? delta > 0 : false;
    return (
      <span>
        <span style={{ color: bad ? OVER : INK_2 }}>{fmtSigned(delta)}</span>
        <span style={{ color: NEUTRAL }}> vs {fmtMoney(before)} last month</span>
      </span>
    );
  };

  const scorecards = totals && (
    <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
      {[
        {
          t: 'Budget',
          v: fmtMoney(totals.budget),
          c: INK,
          sub: movementNote(totals.budget, totals.prevBudget, { higherIsWorse: false }),
        },
        {
          t: 'Actual',
          v: fmtMoney(totals.actual),
          c: INK,
          sub: movementNote(totals.actual, totals.prevActual, { higherIsWorse: true }),
        },
        {
          t: 'Difference',
          v: fmtSigned(totals.variance),
          c: totals.variance == null ? NEUTRAL : (totals.variance < 0 ? OVER : UNDER),
          sub: totals.variance == null
            ? <span style={{ color: NEUTRAL }}>nothing budgeted to compare</span>
            : <span style={{ color: NEUTRAL }}>{totals.variance < 0 ? 'over budget' : 'under budget'}</span>,
        },
        {
          t: 'Bills',
          v: totals.bills,
          c: INK_2,
          sub: <span style={{ color: NEUTRAL }}>authorised or paid</span>,
        },
      ].map((k) => (
        // Four across, splitting the row evenly at every width the page is used
        // at: two up on a phone, four from a tablet.
        <Col xs={12} sm={6} key={k.t}>
          <Card size="small" styles={{ body: { padding: '10px 14px' } }}>
            <Statistic
              title={<span style={{ fontSize: 11 }}>{k.t}</span>}
              value={k.v}
              valueStyle={{ fontSize: 19, color: k.c, fontVariantNumeric: 'tabular-nums' }}
            />
            <div style={{ fontSize: 10.5, marginTop: 2, whiteSpace: 'nowrap' }}>{k.sub}</div>
          </Card>
        </Col>
      ))}
    </Row>
  );

  // ---------------------------------------------------------------- tabs
  /**
   * Fixed panel heights.
   *
   * scroll.y alone is a MAX height, so a table with two rows renders two rows
   * tall and the panel beside it — showing an empty state at full height — ends
   * up twice as long. The body gets an explicit height and the table scrolls
   * inside it, which keeps the pair level whether either is empty, short or
   * overflowing.
   */
  const HEAD_H = 40;
  const ROW_H = 54;
  const FIVE_ROWS = 5 * ROW_H;
  const EIGHT_ROWS = 8 * ROW_H;
  const panelBody = (h) => ({ height: h + HEAD_H, overflow: 'hidden' });

  const supRows = useMemo(() => {
    const all = dashboard?.suppliers || [];
    const needle = supFilter.search.trim().toLowerCase();
    return all.filter((r) => {
      if (supFilter.entity && r.entity !== supFilter.entity) return false;
      if (supFilter.role === 'owner' && !r.isOwner) return false;
      if (supFilter.role === 'backup' && r.isOwner) return false;
      if (needle) {
        // The account name goes in the haystack too: "who do I hold for rent"
        // is the same question as "which supplier", asked the other way round.
        const hay = `${r.name} ${r.costCentre || ''} ${r.learnedCostCentre || ''}`.toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  }, [dashboard, supFilter]);

  const supFilterBar = (() => {
    const all = dashboard?.suppliers || [];
    const entities = [...new Set(all.map((r) => r.entity))].sort();
    const dirty = supFilter.entity || supFilter.role || supFilter.search;
    return (
      <Space wrap size={8} style={{ marginBottom: 10 }}>
        <Input
          size="small" style={{ width: 240 }} allowClear placeholder="Search supplier or account"
          prefix={<SearchOutlined style={{ color: NEUTRAL }} />}
          value={supFilter.search}
          onChange={(e) => setSupFilter((x) => ({ ...x, search: e.target.value }))}
        />
        <Select
          size="small" style={{ width: 150 }} allowClear placeholder="Any entity"
          value={supFilter.entity}
          options={entityOptionsFrom(entities)}
          onChange={(v) => setSupFilter((x) => ({ ...x, entity: v || null }))}
        />
        <Select
          size="small" style={{ width: 170 }} allowClear placeholder="Any role"
          value={supFilter.role}
          options={[
            { value: 'owner', label: `I own it (${all.filter((r) => r.isOwner).length})` },
            { value: 'backup', label: `I am the backup (${all.filter((r) => !r.isOwner).length})` },
          ]}
          onChange={(v) => setSupFilter((x) => ({ ...x, role: v || null }))}
        />
        {dirty && (
          <Button
            size="small" type="link"
            onClick={() => setSupFilter({ entity: null, role: null, search: '' })}
          >
            clear
          </Button>
        )}
        <span style={{ fontSize: 12, color: NEUTRAL }}>
          {supRows.length} of {all.length} suppliers
        </span>
      </Space>
    );
  })();

  /** Everyone I own or back up. Its own tab now — it is a list, not a to-do. */
  const mySuppliersTab = (
    <Card
      size="small"
      title={`My suppliers (${dashboard?.suppliers?.length || 0})`}
      extra={dashboard?.suppliers?.length
        ? <span style={{ fontSize: 11, color: NEUTRAL }}>click a row to set a backup or hand it on</span>
        : null}
    >
      {dashboard?.suppliers?.length ? (
        <>
        {supFilterBar}
        <Table
          size="small" rowKey="id"
          pagination={{ defaultPageSize: 20, showSizeChanger: true }}
          dataSource={supRows}
          locale={{ emptyText: 'No suppliers match these filters' }}
          rowClassName={() => 'budget-row-clickable'}
          onRow={(row) => ({
            onClick: () => {
              setMySup(row);
              setAppointTo(null);
              setSupBackup(row.backupId || null);
            },
          })}
          columns={[
            {
              title: 'Supplier', dataIndex: 'name',
              sorter: (a, b) => String(a.name).localeCompare(String(b.name)),
              render: (v, r) => (
                <span>
                  {v}
                  {r.isStaff && (
                    <Tooltip title="A member of staff — its bills are reimbursements">
                      <Tag color="gold" style={{ marginLeft: 6 }}>staff</Tag>
                    </Tooltip>
                  )}
                </span>
              ),
            },
            {
              title: 'Entity', dataIndex: 'entity', width: 140,
              sorter: (a, b) => entityNameOf(a.entity).localeCompare(entityNameOf(b.entity)),
              render: (v) => entityTag(v),
            },
            {
              title: 'Account name', dataIndex: 'costCentre', width: 190,
              render: (v, r) => v || r.learnedCostCentre
                || <span style={{ color: NEUTRAL }}>not set</span>,
            },
            { title: 'Bills', dataIndex: 'learnedFromBills', width: 80, align: 'right' },
            {
              title: 'My role', dataIndex: 'isOwner', width: 130,
              sorter: (a, b) => Number(b.isOwner) - Number(a.isOwner),
              render: (v, r) => (v
                ? <span style={{ color: INK_2 }}>owner<span style={{ color: NEUTRAL }}>{r.backupName ? ` · backup ${r.backupName}` : ' · no backup'}</span></span>
                : <span style={{ color: NEUTRAL }}>backup for {r.ownerName}</span>),
            },
          ]}
        />
        </>
      ) : (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No suppliers assigned to you yet. Bills route by supplier owner, so nothing will appear here until somebody assigns you one."
        />
      )}
    </Card>
  );

  /**
   * The budget-line filter bar: one control per column of the table below it.
   *
   * Defaulted to the current month because that is the month somebody is
   * working on; every other month is one click away rather than mixed in.
   */
  const lineRows = useMemo(() => {
    const all = dashboard?.myBudgetLines || [];
    const f = lineFilter;
    const needle = f.search.trim().toLowerCase();
    return all.filter((r) => {
      if (f.month && r.month !== f.month) return false;
      if (f.entity && r.entity !== f.entity) return false;
      if (f.origin && (r.origin || 'manual') !== f.origin) return false;
      if (f.account && String(r.accountCode || '') !== f.account) return false;
      if (needle) {
        const hay = `${r.name} ${r.accountName || ''} ${r.accountCode || ''} ${r.entity}`.toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  }, [dashboard, lineFilter]);

  const lineFilterBar = (() => {
    const all = dashboard?.myBudgetLines || [];
    const uniq = (fn) => [...new Set(all.map(fn).filter(Boolean))];
    return (
      <Space wrap size={8} style={{ marginBottom: 10 }}>
        <Select
          size="small" style={{ width: 150 }} allowClear placeholder="Any month"
          value={lineFilter.month}
          options={uniq((r) => r.month).sort().reverse()
            .map((m) => ({ value: m, label: dayjs(m).format('MMM YYYY') }))}
          onChange={(v) => setLineFilter((x) => ({ ...x, month: v || null }))}
        />
        <Select
          size="small" style={{ width: 140 }} allowClear placeholder="Any entity"
          value={lineFilter.entity}
          options={entityOptionsFrom(uniq((r) => r.entity))}
          onChange={(v) => setLineFilter((x) => ({ ...x, entity: v || null }))}
        />
        <Select
          size="small" style={{ width: 150 }} allowClear placeholder="Any source"
          value={lineFilter.origin}
          options={[
            { value: 'estimate', label: 'Estimated' },
            { value: 'manual', label: 'Entered by hand' },
          ]}
          onChange={(v) => setLineFilter((x) => ({ ...x, origin: v || null }))}
        />
        <Select
          size="small" style={{ width: 240 }} allowClear showSearch optionFilterProp="label"
          placeholder="Any account"
          value={lineFilter.account}
          options={uniq((r) => r.accountCode).sort()
            .map((c) => {
              const hit = all.find((r) => r.accountCode === c);
              return { value: c, label: `${c} · ${(hit && hit.accountName) || c}` };
            })}
          onChange={(v) => setLineFilter((x) => ({ ...x, account: v || null }))}
        />
        <Input
          size="small" style={{ width: 200 }} allowClear placeholder="Search supplier or account"
          prefix={<SearchOutlined style={{ color: NEUTRAL }} />}
          value={lineFilter.search}
          onChange={(e) => setLineFilter((x) => ({ ...x, search: e.target.value }))}
        />
        {(lineFilter.month || lineFilter.entity || lineFilter.origin
          || lineFilter.account || lineFilter.search) && (
          <Button
            size="small" type="link"
            onClick={() => setLineFilter({ month: null, entity: null, origin: null, account: null, search: '' })}
          >
            clear
          </Button>
        )}
        <span style={{ fontSize: 12, color: NEUTRAL }}>
          {lineRows.length} of {all.length} lines
        </span>
      </Space>
    );
  })();

  const myDay = (
    <Row gutter={[12, 12]}>
      <Col xs={24} lg={12}>
        <Card
          size="small"
          title={`Waiting on me (${dashboard?.pendingApprovals?.length || 0})`}
          /**
           * When Xero was last read, and a way to read it again.
           *
           * Without this the queue is a number with no provenance: somebody
           * looking at "All done!" cannot tell whether that means there is
           * nothing to do or that nothing has been checked.
           */
          extra={(
            <Space size={8}>
              {queueSync?.running && (
                <span style={{ fontSize: 11, color: NEUTRAL }}>
                  <Spin size="small" style={{ marginRight: 6 }} />
                  checking Xero across all entities…
                </span>
              )}
              {!queueSync?.running && queueSync?.error && (
                <Tooltip title={queueSync.error}>
                  <span style={{ fontSize: 11, color: OVER }}>Xero check failed</span>
                </Tooltip>
              )}
              {!queueSync?.running && !queueSync?.error && queueSync?.at && (
                <Tooltip
                  title={(queueSync.result?.entities || [])
                    .map((e) => `${e.name || e.entity}: ${
                      e.error ? `failed — ${e.error}`
                        : e.skipped === 'fresh' ? 'already current'
                          : e.skipped === 'no_time' ? 'not reached this time'
                            : `${e.bills} submitted, ${e.mine} yours`}`)
                    .join('\n') || null}
                >
                  <span style={{ fontSize: 11, color: NEUTRAL }}>
                    Xero checked {dayjs(queueSync.at).format('HH:mm')}
                    {queueSync.result?.failed?.length
                      ? ` · ${queueSync.result.failed.length} entity failed`
                      : ''}
                  </span>
                </Tooltip>
              )}
              <Tooltip title="Read every entity's submitted bills from Xero again now">
                <Button
                  size="small" type="text" icon={<ReloadOutlined />}
                  loading={queueSync?.running}
                  onClick={() => refreshQueues({ force: true })}
                />
              </Tooltip>
            </Space>
          )}
        >
          <div style={panelBody(FIVE_ROWS)}>
          {dashboard?.pendingApprovals?.length ? (
            <Table
              size="small" rowKey="id" pagination={false}
              scroll={{ y: FIVE_ROWS }}
              dataSource={dashboard.pendingApprovals}
              onRow={(r) => ({
                style: { cursor: 'pointer' },
                // Straight to the queue it is in, on the month it belongs to,
                // rather than leaving somebody to find it by hand.
                onClick: () => {
                  setEntity(r.entity);
                  if (r.billDate) setMonth(dayjs(r.billDate).startOf('month'));
                  setEntityTab('bills');
                  setTab('entity');
                },
              })}
              columns={[
                {
                  title: 'Supplier',
                  dataIndex: 'supplierName',
                  render: (v, r) => (
                    <div>
                      <div style={{ color: INK }}>{v}</div>
                      <div style={{ fontSize: 11, color: NEUTRAL }}>
                        {r.accountName || r.invoiceNumber || '—'}
                      </div>
                    </div>
                  ),
                },
                { title: 'Entity', dataIndex: 'entity', width: 140, render: (v) => entityTag(v) },
                {
                  title: 'Amount', dataIndex: 'amount', width: 105, align: 'right',
                  render: (v) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(v)}</span>,
                },
                {
                  title: 'Why', dataIndex: 'why', width: 150,
                  render: (v, r) => (
                    <div>
                      <div style={{ fontSize: 12 }}>{v}</div>
                      {r.stage === 'executive' && (
                        <Tag color="red" style={{ margin: 0, fontSize: 10 }}>needs an executive</Tag>
                      )}
                    </div>
                  ),
                },
                {
                  title: 'Due', dataIndex: 'dueDate', width: 95,
                  render: (v) => {
                    if (!v) return <span style={{ color: NEUTRAL }}>—</span>;
                    const late = dayjs(v).isBefore(dayjs(), 'day');
                    return (
                      <span style={{ color: late ? OVER : INK }}>
                        {dayjs(v).format('D MMM')}
                      </span>
                    );
                  },
                },
              ]}
            />
          ) : (
            // An empty queue is the good outcome, not missing data, so it gets
            // a tick rather than the empty-box illustration Empty uses.
            <div style={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 10,
            }}
            >
              <CheckCircleFilled style={{ fontSize: 38, color: UNDER }} />
              <span style={{ fontSize: 15, fontWeight: 600, color: UNDER }}>All done!</span>
            </div>
          )}
          </div>
        </Card>
      </Col>

      <Col xs={24} lg={12}>
        <Card size="small" title={`Recently approved (${dashboard?.recentDecisions?.length || 0})`}>
          <div style={panelBody(FIVE_ROWS)}>
          {dashboard?.recentDecisions?.length ? (
            <Table
              size="small" rowKey="id" pagination={false}
              scroll={{ y: FIVE_ROWS }}
              dataSource={dashboard.recentDecisions}
              columns={[
                {
                  title: 'Supplier', dataIndex: 'supplierName',
                  render: (v, r) => (
                    <div>
                      <div style={{ color: INK }}>{v}</div>
                      <div style={{ fontSize: 11, color: NEUTRAL }}>
                        {r.accountName || r.invoiceNumber || '—'}
                        {r.tier && (
                          <Tooltip title={r.tier === 'executive'
                            ? 'Signed as the over-run approver — this is the signature that finished it'
                            : 'Signed as the supplier owner. An over-budget bill then goes up, so the same bill can carry both of your signatures'}
                          >
                            <span style={{ marginLeft: 6 }}>· as {r.tier}</span>
                          </Tooltip>
                        )}
                      </div>
                    </div>
                  ),
                },
                { title: 'Entity', dataIndex: 'entity', width: 140, render: (v) => entityTag(v) },
                {
                  title: 'Amount', dataIndex: 'amount', width: 110, align: 'right',
                  render: (v) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(v)}</span>,
                },
                {
                  title: 'Pay on', dataIndex: 'plannedPaymentDate', width: 90,
                  render: (v) => (v
                    ? <span style={{ fontSize: 12 }}>{dayjs(v).format('D MMM')}</span>
                    : <span style={{ color: NEUTRAL }}>—</span>),
                },
                {
                  title: '', dataIndex: 'decision', width: 90,
                  render: (v, r) => (
                    <Tooltip title={dayjs(r.decidedAt).format('D MMM YYYY HH:mm')}>
                      <Tag color={v === 'approved' ? 'green' : 'red'} style={{ margin: 0 }}>{v}</Tag>
                    </Tooltip>
                  ),
                },
              ]}
            />
          ) : (
            <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="You have not decided anything yet" />
            </div>
          )}
          </div>
        </Card>
      </Col>

      <Col span={24}>
        <Card
          size="small"
          title={`Budget lines I raised (${dashboard?.myBudgetLines?.length || 0})`}
          extra={(
            <Space size={12}>
              <span style={{ fontSize: 11, color: NEUTRAL }}>click a row in an open month to change it</span>
              <Button
                size="small" type="primary" icon={<PlusOutlined />}
                style={{ background: '#54A2CC', borderColor: '#54A2CC' }}
                disabled={!dashboard?.suppliers?.length}
                onClick={() => {
                  const first = (dashboard.suppliers || [])[0];
                  setNewLine({
                    entity: first ? first.entity : null,
                    supplierId: null,
                    accountCode: '', accountName: '',
                    amount: null,
                    month: dayjs(dashboard.month),
                    frequency: 'one_off', months: 1, note: '',
                  });
                  if (first) ensureAccounts(first.entity);
                  setNewOpen(true);
                }}
              >
                New budget
              </Button>
            </Space>
          )}
        >
          {lineFilterBar}
          <Table
            size="small" rowKey="id"
            pagination={false}
            scroll={{ y: EIGHT_ROWS, x: 1000 }}
            dataSource={lineRows}
            rowClassName={(r) => (r.monthLocked ? '' : 'budget-row-clickable')}
            onRow={(row) => ({
              onClick: () => {
                if (row.monthLocked) {
                  message.info(`${dayjs(row.month).format('MMMM YYYY')} is locked, so this line cannot be changed`);
                  return;
                }
                setMyLine(row);
                setMyLineAmount(row.granted ?? row.requested);
                setMyLineNote(row.note || '');
                setMyLineMonth(dayjs(row.month));
                setMyLineFreq(row.frequency || 'one_off');
                setMyLineAccount({ code: row.accountCode || '', name: row.accountName || '' });
                ensureAccounts(row.entity);
              },
            })}
            locale={{ emptyText: 'No budget lines match these filters' }}
            // Totals of what is filtered, not of everything — the number under a
            // filtered column has to be the sum of what is above it.
            summary={(rows) => {
              const budgeted = rows.reduce((t, r) => t + (r.requested || 0), 0);
              const approved = rows.reduce((t, r) => t + (r.approvedAmount || 0), 0);
              const bills = rows.reduce((t, r) => t + (r.approvedBills || 0), 0);
              return (
                <Table.Summary fixed>
                  <Table.Summary.Row style={{ background: '#f7f9fb', fontWeight: 600 }}>
                    <Table.Summary.Cell index={0} colSpan={5}>
                      <span style={{ color: INK_2 }}>
                        {rows.length} line{rows.length === 1 ? '' : 's'}
                      </span>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={5} align="right">
                      <span style={{ fontVariantNumeric: 'tabular-nums', color: INK }}>
                        {fmtMoney(budgeted)}
                      </span>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={6} align="right">
                      <span style={{ fontVariantNumeric: 'tabular-nums', color: approved > budgeted ? OVER : INK_2 }}>
                        {fmtMoney(approved)}
                        {bills > 0 && (
                          <span style={{ fontSize: 11, color: NEUTRAL, fontWeight: 400 }}> · {bills}</span>
                        )}
                      </span>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                </Table.Summary>
              );
            }}
            columns={[
              {
                title: 'Month', dataIndex: 'month', width: 120,
                sorter: (a, b) => String(a.month).localeCompare(String(b.month)),
                render: (v, r) => (
                  <span>
                    {dayjs(v).format('MMM YYYY')}
                    {r.monthLocked && (
                      <Tooltip title="Locked, so this line is settled">
                        <LockOutlined style={{ marginLeft: 6, color: NEUTRAL }} />
                      </Tooltip>
                    )}
                  </span>
                ),
              },
              {
                title: 'Supplier', dataIndex: 'name', width: 210,
                sorter: (a, b) => String(a.name).localeCompare(String(b.name)),
                render: (v, r) => (r.dimension === 'supplier'
                  ? <span style={{ color: INK }}>{v}</span>
                  : <span style={{ color: NEUTRAL }}>— any payee —</span>),
              },
              { title: 'Entity', dataIndex: 'entity', width: 140, render: (v) => entityTag(v) },
              {
                // Source, not dimension. The dimension was already legible from
                // the Supplier column, which reads "— any payee —" on an account
                // line; what could not be seen anywhere is whether the next
                // estimate will overwrite this figure.
                title: 'Source', dataIndex: 'origin', width: 110,
                filters: [
                  { text: 'Estimated', value: 'estimate' },
                  { text: 'Entered by hand', value: 'manual' },
                ],
                onFilter: (v, r) => (r.origin || 'manual') === v,
                render: (v) => (v === 'estimate'
                  ? (
                    <Tooltip title="Generated by the estimate, and the next estimate will replace it. Changing it by hand makes it yours and leaves it alone from then on.">
                      <Tag style={{ margin: 0 }}>estimated</Tag>
                    </Tooltip>
                  )
                  : (
                    <Tooltip title="Entered by hand. The estimate will neither replace it nor duplicate it.">
                      <Tag color="cyan" style={{ margin: 0 }}>manual</Tag>
                    </Tooltip>
                  )),
              },
              {
                // "not set" only when there is genuinely no account. A line with a
                // code and no name is named — we just have not resolved the name
                // yet — and calling that "not set" sends somebody to fix a field
                // that is already filled in.
                title: 'Account', dataIndex: 'accountName', width: 200,
                render: (v, r) => {
                  if (!r.accountCode && !v) return <span style={{ color: NEUTRAL }}>not set</span>;
                  return (
                    <span>
                      {v || r.accountCode}
                      {v && <div style={{ fontSize: 11, color: NEUTRAL }}>{r.accountCode}</div>}
                    </span>
                  );
                },
              },
              {
                title: 'Budget $', dataIndex: 'requested', width: 120, align: 'right',
                defaultSortOrder: 'descend',
                sorter: (a, b) => a.requested - b.requested,
                render: (v) => <span style={{ fontVariantNumeric: 'tabular-nums', color: INK }}>{fmtMoney(v)}</span>,
              },
              {
                title: 'Approved $', dataIndex: 'approvedAmount', width: 140, align: 'right',
                sorter: (a, b) => a.approvedAmount - b.approvedAmount,
                render: (v, r) => (r.approvedBills
                  ? (
                    <Tooltip title={`${r.approvedBills} bill${r.approvedBills === 1 ? '' : 's'} approved against this line`}>
                      <span style={{ fontVariantNumeric: 'tabular-nums', color: v > r.requested ? OVER : INK_2 }}>
                        {fmtMoney(v)}
                        <span style={{ fontSize: 11, color: NEUTRAL }}> · {r.approvedBills}</span>
                      </span>
                    </Tooltip>
                  )
                  : <span style={{ color: NEUTRAL }}>—</span>),
              },
            ]}
          />
        </Card>
      </Col>
    </Row>
  );

  const entityTabs = (
    <Tabs
      size="small"
      activeKey={entityTab}
      onChange={setEntityTab}
      items={[
        {
          key: 'tracking',
          label: 'Budget vs actual',
          children: (
            <>
              {unlockBanner}
              {exclusionNote}
              {scorecards}
              <Card
                size="small"
                title="By supplier"
                extra={!locked && current?.canApply && (
                  <span style={{ fontSize: 11, color: NEUTRAL }}>click a row to change its budget</span>
                )}
                style={{ marginBottom: 12 }}
                styles={{ body: { padding: 0 } }}
              >
                <Table
                  size="small" rowKey="key" scroll={{ x: 1000 }}
                  rowClassName={() => (locked || !current?.canApply ? '' : 'budget-row-clickable')}
                  onRow={(row) => ({ onClick: () => openRow(row, 'supplier') })}
                  dataSource={overview?.suppliers || []}
                  columns={trackingColumns('Supplier')}
                  pagination={{ defaultPageSize: 20, showSizeChanger: true, pageSizeOptions: ['20', '50', '100'] }}
                  locale={{ emptyText: 'No budget lines and no spend for this month' }}
                />
              </Card>
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 12 }}
                message={(
                  <span style={{ fontSize: 12 }}>
                    An account&apos;s budget is the sum of its supplier lines unless somebody sets a
                    figure on the account itself — a cap below that sum, or, on an envelope account,
                    the whole monthly commitment. Nothing is typed twice.
                  </span>
                )}
              />

              <Card
                size="small"
                title="By expense account"
                extra={(
                  <Space size={12}>
                    {totals?.budgetUnattributed > 0 && (
                      <Tooltip title="Supplier budget lines that name no expense account, so they cannot be attributed to a category. Open the line and set its account to have it counted here.">
                        <span style={{ fontSize: 11, color: OVER }}>
                          {fmtMoney(totals.budgetUnattributed)} not attributed to an account
                        </span>
                      </Tooltip>
                    )}
                    {!locked && current?.canApply && (
                      <span style={{ fontSize: 11, color: NEUTRAL }}>click a row to change its budget</span>
                    )}
                  </Space>
                )}
                styles={{ body: { padding: 0 } }}
              >
                <Table
                  size="small" rowKey="key" scroll={{ x: 1000 }}
                  rowClassName={() => (locked || !current?.canApply ? '' : 'budget-row-clickable')}
                  onRow={(row) => ({ onClick: () => openRow(row, 'account') })}
                  dataSource={overview?.accounts || []}
                  columns={trackingColumns('Account')}
                  pagination={{ defaultPageSize: 20, showSizeChanger: true }}
                  locale={{ emptyText: 'Nothing coded to an account this month' }}
                />
              </Card>
            </>
          ),
        },
        {
          key: 'suppliers',
          label: `Suppliers (${suppliers.length})`,
          children: (
            <Card
              size="small"
              styles={{ body: { padding: 0 } }}
              title={(
                <span style={{ fontSize: 12, fontWeight: 400, color: INK_2 }}>
                  Every contact Xero regards as a supplier, plus anyone in a &ldquo;Portal
                  suppliers&rdquo; contact group and anyone with an opex bill. The default account is
                  learned from how their bills were coded; owners are set in People &amp; access.
                </span>
              )}
              extra={current?.canApply && (
                <Button
                  size="small" loading={syncing}
                  onClick={async () => {
                    setSyncing(true);
                    try {
                      const r = await api.post('/api/budget/suppliers/sync', { entity }, { timeout: 300000 });
                      const d = r.data.data;
                      const src = d.bySource || {};
                      message.success(
                        `${d.found} suppliers — ${d.inserted} added, ${d.updated} updated, `
                        + `${d.madeDormant} dormant. From: ${src.contact || 0} Xero supplier flag, `
                        + `${src.group || 0} contact group, ${src.bills || 0} bills only.`,
                        8,
                      );
                      if (!(d.supplierGroups || []).length) {
                        // Without a group there is no way to budget for a vendor
                        // before its first bill, because Xero's own supplier flag
                        // is derived from having been billed and cannot be set.
                        message.info(
                          'No "Portal suppliers" contact group found in this Xero organisation. '
                          + 'Create one and add new vendors to it to budget for them before their first bill.',
                          10,
                        );
                      }
                      loadEntity();
                    } catch (err) {
                      message.error(err.response?.data?.message || 'Sync failed');
                    }
                    setSyncing(false);
                  }}
                >
                  sync from Xero
                </Button>
              )}
            >
              <Table
                size="small" rowKey="id" dataSource={suppliers}
                rowClassName={() => 'budget-row-clickable'}
                onRow={(row) => ({
                  onClick: () => {
                    setSupRow(row);
                    setSupDraft({
                      ownerId: row.ownerId || null,
                      backupId: row.backupId || null,
                      accountName: row.costCentre || row.learnedCostCentre || '',
                    });
                  },
                })}
                pagination={{ defaultPageSize: 20, showSizeChanger: true }}
                locale={{ emptyText: 'No suppliers recorded for this entity yet' }}
                columns={[
                  {
                    title: 'Supplier', dataIndex: 'name', width: 280,
                    render: (v, r) => (
                      <div>
                        <div style={{ color: INK }}>
                          {v}
                          {r.isStaff && (
                            <Tooltip title="This contact is a member of staff, so its bills are reimbursements. They cannot approve their own.">
                              <Tag color="gold" style={{ marginLeft: 6 }}>staff</Tag>
                            </Tooltip>
                          )}
                        </div>
                        {r.source === 'group' && (
                          <Tooltip title="Declared a supplier by contact-group membership, so it can be budgeted for before its first bill">
                            <div style={{ fontSize: 11, color: UNDER }}>declared in Xero group</div>
                          </Tooltip>
                        )}
                        {r.source === 'bills' && r.xeroIsSupplier === null && (
                          <Tooltip title="Found only through its bills — Xero does not currently list it as a supplier contact">
                            <div style={{ fontSize: 11, color: NEUTRAL }}>from bills only</div>
                          </Tooltip>
                        )}
                      </div>
                    ),
                  },
                  {
                    title: 'Owner', dataIndex: 'ownerName', width: 180,
                    render: (v, r) => (v
                      ? <span>{v}{r.backupName ? <span style={{ color: NEUTRAL }}> · backup {r.backupName}</span> : null}</span>
                      : (
                        <Tooltip title="Bills route by supplier owner. Without one, this supplier's bills have nowhere to go.">
                          <Tag color="orange" style={{ margin: 0 }}>unassigned</Tag>
                        </Tooltip>
                      )),
                  },
                  {
                    title: 'Account name', dataIndex: 'costCentre', width: 200,
                    render: (v, r) => v || (r.learnedCostCentre
                      ? (
                        <Tooltip title={`Suggested from ${r.learnedFromBills} past bills`}>
                          <span style={{ color: NEUTRAL }}>{r.learnedCostCentre} (suggested)</span>
                        </Tooltip>
                      )
                      : <span style={{ color: NEUTRAL }}>not set</span>),
                  },
                  {
                    title: 'Active', dataIndex: 'active', width: 90,
                    render: (v) => (v ? <Tag color="green">active</Tag> : <Tag>dormant</Tag>),
                  },
                ]}
              />
            </Card>
          ),
        },
        {
          key: 'bills',
          label: inbox ? `Bill inbox (${inbox.totals.count})` : 'Bill inbox',
          children: (
            <>
              <Card size="small" style={{ marginBottom: 12 }} styles={{ body: { padding: '10px 14px' } }}>
                <Space wrap size="middle" style={{ width: '100%', justifyContent: 'space-between' }}>
                  <Space wrap size="middle">
                    {inboxLoading && (
                      <Space size={8}>
                        <Spin size="small" />
                        <span style={{ fontSize: 12, color: NEUTRAL }}>reading submitted bills from Xero…</span>
                      </Space>
                    )}
                    {/* Only reachable if the load failed: the tab loads itself. */}
                    {!inbox && !inboxLoading && (
                      <span style={{ fontSize: 12, color: NEUTRAL }}>
                        Nothing loaded — use refresh above.
                      </span>
                    )}
                    {inbox && (
                      <Segmented
                        size="small"
                        value={mineOnly ? 'mine' : 'all'}
                        onChange={(v) => setMineOnly(v === 'mine')}
                        options={[
                          { label: `All (${inbox.totals.count})`, value: 'all' },
                          { label: `Mine (${inbox.bills.filter((b) => b.mine).length})`, value: 'mine' },
                        ]}
                      />
                    )}
                  </Space>
                  {inbox && (
                    <Space size={14} wrap style={{ fontSize: 12, color: INK_2 }}>
                      <span>{fmtMoney(inbox.totals.opexAmount)} opex</span>
                      {inbox.totals.needExecutive > 0 && (
                        <span style={{ color: OVER }}>
                          <WarningOutlined /> {inbox.totals.needExecutive} need an executive
                        </span>
                      )}
                      {inbox.totals.unknownSupplier > 0 && (
                        <span style={{ color: NEUTRAL }}>{inbox.totals.unknownSupplier} unknown supplier</span>
                      )}
                      {inbox.totals.noOpexBills > 0 && (
                        <Tooltip title="Stock, freight and other cost-of-sales or balance-sheet bills. They have no opex to check against a budget and no supplier owner to route to, so there is nothing for this queue to do with them — Finance authorises those in Xero.">
                          <span style={{ color: NEUTRAL }}>
                            {inbox.totals.noOpexBills} non-opex bill
                            {inbox.totals.noOpexBills === 1 ? '' : 's'} left out
                            {inbox.totals.noOpexAmount > 0 && ` (${fmtMoney(inbox.totals.noOpexAmount)})`}
                          </span>
                        </Tooltip>
                      )}
                      {inbox.totals.reimbursements > 0 && (
                        <span style={{ color: INK_2 }}>{inbox.totals.reimbursements} payable to staff</span>
                      )}
                      {inbox.totals.onEnvelope > 0 && (
                        <span style={{ color: NEUTRAL }}>{inbox.totals.onEnvelope} on a monthly envelope</span>
                      )}
                      {inbox.totals.routedByAccount > 0 && (
                        <span style={{ color: NEUTRAL }}>{inbox.totals.routedByAccount} routed by account rule</span>
                      )}
                      {inbox.totals.unowned > 0 && (
                        <span style={{ color: NEUTRAL }}>{inbox.totals.unowned} with no owner</span>
                      )}
                    </Space>
                  )}
                </Space>
              </Card>

              <Alert
                type="info" showIcon style={{ marginBottom: 12 }}
                message="Approving a bill here authorises it in Xero"
                description="Only bills marked submitted for approval in Xero appear — while a bill is a draft the amount and the coding are still moving. Approving sets it to AUTHORISED in Xero, which is what puts it in Finance's payment run; rejecting and handing on touch nothing in the books. A bill already authorised, paid or voided in Xero is left exactly as it is."
              />

              <Card size="small" styles={{ body: { padding: 0 } }}>
                <Table
                  size="small"
                  rowKey="reviewId"
                  loading={inboxLoading}
                  scroll={{ x: 1200 }}
                  dataSource={(inbox?.bills || []).filter((b) => !mineOnly || b.mine)}
                  pagination={{ defaultPageSize: 20, showSizeChanger: true }}
                  locale={{ emptyText: inbox ? 'Nothing awaiting a decision' : 'Press load to fetch this month\'s bills' }}
                  columns={[
                    {
                      title: 'Supplier', dataIndex: 'supplierName', fixed: 'left', width: 230,
                      render: (v, r) => (
                        <div>
                          <div style={{ color: INK }}>{v}</div>
                          <div style={{ fontSize: 11, color: NEUTRAL }}>
                            {r.invoiceNumber || 'no number'} · {r.date}
                            {r.payeeIsUser && (
                              <Tooltip title={r.payeeIsYou
                                ? 'Payable to you. You cannot decide this one — reassign it.'
                                : 'Payable to a member of staff, so this is a reimbursement rather than a vendor bill'}
                              >
                                <Tag color={r.payeeIsYou ? 'red' : 'gold'} style={{ marginLeft: 6 }}>
                                  {r.payeeIsYou ? 'payable to you' : 'reimbursement'}
                                </Tag>
                              </Tooltip>
                            )}

                          </div>
                        </div>
                      ),
                    },
                    {
                      title: 'Opex (AUD ex-GST)', dataIndex: 'opexAmount', width: 150, align: 'right',
                      sorter: (a, b) => a.opexAmount - b.opexAmount,
                      defaultSortOrder: 'descend',
                      render: (v, r) => (
                        <div>
                          <div style={{ fontVariantNumeric: 'tabular-nums', color: INK }}>{fmtMoney(v)}</div>
                          {r.excludedAmount > 0 && (
                            <Tooltip title="Cost of sales or balance-sheet lines on the same bill, not counted as opex">
                              <div style={{ fontSize: 11, color: NEUTRAL }}>+{fmtMoney(r.excludedAmount)} non-opex</div>
                            </Tooltip>
                          )}
                        </div>
                      ),
                    },
                    {
                      title: 'Coded to', dataIndex: 'costCentre', width: 190,
                      render: (v, r) => (v
                        ? (
                          <span>
                            {v}
                            <span style={{ color: NEUTRAL, fontSize: 11 }}> · {r.costCentreSource}</span>
                          </span>
                        )
                        : <Tag color="orange" style={{ margin: 0 }}>not coded</Tag>),
                    },
                    {
                      title: 'Goes to', dataIndex: 'ownerName', width: 160,
                      render: (v, r) => {
                        if (!r.knownSupplier) {
                          return (
                            <Tooltip title="This supplier is not in the portal yet — sync or add it, then assign an owner">
                              <Tag color="red" style={{ margin: 0 }}>unknown supplier</Tag>
                            </Tooltip>
                          );
                        }
                        if (!v) {
                          return (
                            <Tooltip title="Assign an owner in People & access; until then this bill has nowhere to go">
                              <Tag color="orange" style={{ margin: 0 }}>no owner</Tag>
                            </Tooltip>
                          );
                        }
                        return (
                          <span>
                            {v}
                            {r.mine && <Tag color="blue" style={{ marginLeft: 6 }}>you</Tag>}
                            {r.routedVia === 'account' && (
                              <Tooltip title={`Routed by the account rule for ${r.routedViaAccount}, since this supplier has no owner of its own`}>
                                <div style={{ fontSize: 11, color: NEUTRAL }}>via {r.routedViaAccount}</div>
                              </Tooltip>
                            )}
                            {r.routedVia === 'reassigned' && (
                              <Tooltip title="Handed to them for this bill only — the supplier keeps its own owner">
                                <div style={{ fontSize: 11, color: NEUTRAL }}>handed on</div>
                              </Tooltip>
                            )}
                            {r.routedVia === 'payee_blocked' && (
                              <Tooltip title="Everyone this would route to is the person being paid. Somebody who can approve an over-run has to take it.">
                                <div style={{ fontSize: 11, color: OVER }}>payee is the approver</div>
                              </Tooltip>
                            )}
                          </span>
                        );
                      },
                    },
                    {
                      title: 'Budget check', width: 260,
                      render: (_, r) => (
                        <Tooltip title={r.check.detail}>
                          <Space direction="vertical" size={0}>
                            <Tag
                              color={r.check.outcome === 'within' ? 'green' : (r.check.outcome === 'over_supplier' || r.check.outcome === 'over_account' ? 'red' : 'orange')}
                              style={{ margin: 0 }}
                            >
                              {r.check.outcome === 'within' && r.basis === 'account'
                                ? 'within envelope'
                                : {
                                  within: 'within budget',
                                  over_supplier: 'over supplier line',
                                  over_account: 'over account envelope',
                                  no_budget: r.basis === 'account' ? 'no envelope set' : 'no budget line',
                                  no_locked_period: 'month not locked',
                                }[r.check.outcome]}
                            </Tag>
                            <span style={{ fontSize: 11, color: NEUTRAL }}>{r.check.detail}</span>
                          </Space>
                        </Tooltip>
                      ),
                    },
                    {
                      title: 'Pay on', dataIndex: 'plannedPaymentDate', width: 110,
                      render: (v, r) => {
                        if (v) {
                          const off = r.dueDate && !dayjs(v).isSame(dayjs(r.dueDate), 'day');
                          return (
                            <Tooltip title={off ? `Approved for a date other than its due date, ${dayjs(r.dueDate).format('D MMM')}` : 'As due'}>
                              <span style={{ color: off ? OVER : INK }}>{dayjs(v).format('D MMM')}</span>
                            </Tooltip>
                          );
                        }
                        return <span style={{ color: NEUTRAL }}>{r.dueDate ? `due ${dayjs(r.dueDate).format('D MMM')}` : '—'}</span>;
                      },
                    },
                    {
                      title: 'Stage', dataIndex: 'stage', width: 110,
                      render: (v, r) => (r.decided
                        ? <Tag color={r.decided === 'approved' ? 'green' : 'red'}>{r.decided}</Tag>
                        : <Tag color={v === 'executive' ? 'volcano' : 'blue'}>{v}</Tag>),
                    },
                    {
                      title: '', fixed: 'right', width: 170,
                      render: (_, r) => {
                        if (r.decided) return <span style={{ color: NEUTRAL, fontSize: 11 }}>closed</span>;
                        const blocked = (r.check.needsExecutive && r.stage === 'executive' && !inbox.canApproveOverspend)
                          || r.payeeIsYou;
                        return (
                          <Space size={4}>
                            <Tooltip title={(() => {
                              if (r.payeeIsYou) return 'This is payable to you — somebody else has to decide it';
                              if (blocked) return 'Over budget — needs someone who can approve an over-run';
                              return r.check.needsExecutive ? 'Approving sends this to the executive' : 'Approve';
                            })()}
                            >
                              <Button
                                size="small" type="primary" loading={deciding === r.reviewId}
                                disabled={blocked}
                                style={blocked ? undefined : { background: UNDER, borderColor: UNDER }}
                                onClick={() => setApproving({
                                  row: r,
                                  // The due date is what Finance would schedule
                                  // anyway, so it is the answer unless somebody
                                  // deliberately changes it.
                                  date: r.dueDate ? dayjs(r.dueDate) : dayjs(),
                                })}
                              >
                                approve
                              </Button>
                            </Tooltip>
                            <Button
                              size="small" danger loading={deciding === r.reviewId}
                              disabled={r.payeeIsYou}
                              onClick={() => decide(r, 'rejected')}
                            >
                              reject
                            </Button>
                            <Tooltip title="Give this bill to somebody else to decide. Its supplier keeps its owner; only this bill moves.">
                              <Button
                                size="small" loading={deciding === r.reviewId}
                                onClick={() => setHanding({ row: r, toUserId: null })}
                              >
                                hand on
                              </Button>
                            </Tooltip>
                          </Space>
                        );
                      },
                    },
                  ]}
                />
              </Card>
            </>
          ),
        },
      ]}
    />
  );

  /**
   * One row per person, one column group per entity. A grid rather than a form
   * per person: the question this screen answers is "who can do what where",
   * and that is only visible when everyone is on screen together — including the
   * people with nothing, who are exactly the ones needing a grant.
   */
  const RIGHTS = [
    { key: 'canApply', label: 'apply', hint: 'Raise budget lines, add suppliers, record expected costs' },
    { key: 'canApproveOverspend', label: 'over', hint: 'Approve a bill that breaches its budget' },
    { key: 'canLock', label: 'lock', hint: 'Lock a month, and see the full entity report' },
  ];

  const saveRule = async () => {
    if (!ruleDraft.entity || !ruleDraft.accountCode || !ruleDraft.ownerId) {
      message.warning('Entity, account code and owner are all needed');
      return;
    }
    setSavingRule(true);
    try {
      await api.put('/api/budget/account-policy', ruleDraft);
      setRuleDraft({
        entity: null, accountCode: '', accountName: '', ownerId: null, budgetBasis: 'supplier',
      });
      await Promise.all([loadAccountOwners(), loadUnassigned()]);
      message.success('Rule saved — bills coded to that account now route to them');
    } catch (err) {
      message.error(err.response?.data?.message || 'Could not save the rule');
    }
    setSavingRule(false);
  };

  // The email is part of the label rather than a subtitle: two people can share
  // a name, and picking the wrong one hands a supplier's payments to somebody
  // who was never meant to see them.
  const ownerOptions = (people?.people || []).map((p) => ({
    value: p.id,
    label: p.email && p.email !== p.name ? `${p.name} · ${p.email}` : p.name,
  }));

  /**
   * Account-level routing. This exists because 165 of ShineHub's suppliers are
   * individual customers receiving a referral gift card — assigning owners one at
   * a time would never finish, and every new referral would add another.
   */
  const rulesPanel = (
    <Card
      size="small"
      style={{ marginBottom: 12 }}
      title="Account rules"
      styles={{ body: { padding: 0 } }}
    >
      <div style={{ padding: '8px 14px', fontSize: 12, color: INK_2, borderBottom: '1px solid #f0f0f0' }}>
        Two things per account: who approves its bills, and how it is budgeted.
        <div style={{ marginTop: 6 }}>
          <b>Per supplier</b> — the commitment names a vendor and each bill is checked against that
          vendor&apos;s line. Rent, subscriptions, a retainer.
        </div>
        <div>
          <b>Monthly envelope</b> — the account has one figure a month and any payee draws it down.
          Travel, where nobody can say in advance which airline and the payee may be an employee
          reclaiming a ticket they bought themselves. Without this every such bill reports &ldquo;no
          budget line for this supplier&rdquo; and escalates.
        </div>
      </div>
      <div style={{ padding: '10px 14px', borderBottom: '1px solid #f0f0f0' }}>
        <Space wrap size={8}>
          <Select
            size="small" style={{ width: 150 }} placeholder="entity"
            value={ruleDraft.entity}
            options={(people?.entities || []).map((e) => ({ value: e.code, label: e.name }))}
            onChange={(v) => setRuleDraft((d) => ({ ...d, entity: v }))}
          />
          <div style={{ width: 300 }}>
            {accountPicker(
              ruleDraft.entity,
              ruleDraft.accountCode,
              (code, name) => setRuleDraft((d) => ({ ...d, accountCode: code || '', accountName: name || '' })),
              { size: 'small' },
            )}
          </div>
          <Select
            size="small" style={{ width: 170 }}
            value={ruleDraft.budgetBasis}
            options={[
              { value: 'supplier', label: 'per supplier' },
              { value: 'account', label: 'monthly envelope' },
            ]}
            onChange={(v) => setRuleDraft((d) => ({ ...d, budgetBasis: v }))}
          />
          <Select
            size="small" showSearch optionFilterProp="label" style={{ width: 190 }} placeholder="routes to"
            value={ruleDraft.ownerId} options={ownerOptions}
            onChange={(v) => setRuleDraft((d) => ({ ...d, ownerId: v }))}
          />
          <Button
            size="small" type="primary" loading={savingRule} onClick={saveRule}
            style={{ background: '#54A2CC', borderColor: '#54A2CC' }}
          >
            add rule
          </Button>
        </Space>
      </div>
      <Table
        size="small" rowKey="id" pagination={false}
        dataSource={accountOwners || []}
        loading={!accountOwners}
        locale={{ emptyText: 'No account rules yet' }}
        columns={[
          { title: 'Entity', dataIndex: 'entity', width: 140, render: (v) => entityTag(v) },
          {
            title: 'Account', dataIndex: 'accountName', width: 260,
            render: (v, r) => <span>{v || r.accountCode}<span style={{ color: NEUTRAL, fontSize: 11 }}> · {r.accountCode}</span></span>,
          },
          {
            title: 'Budgeted', dataIndex: 'budgetBasis', width: 150,
            render: (v) => (v === 'account'
              ? <Tag color="geekblue" style={{ margin: 0 }}>monthly envelope</Tag>
              : <span style={{ color: NEUTRAL }}>per supplier</span>),
          },
          { title: 'Routes to', dataIndex: 'ownerName', width: 180 },
          {
            title: 'Covers', dataIndex: 'coversUnownedSuppliers', width: 200,
            render: (v) => (v > 0
              ? <span style={{ color: UNDER }}>{v} suppliers with no owner of their own</span>
              : <span style={{ color: NEUTRAL }}>—</span>),
          },
          {
            title: '', width: 80,
            render: (_, r) => (
              <Button
                size="small" danger
                onClick={async () => {
                  await api.put('/api/budget/account-policy', { entity: r.entity, accountCode: r.accountCode, ownerId: null });
                  await Promise.all([loadAccountOwners(), loadUnassigned()]);
                }}
              >
                remove
              </Button>
            ),
          },
        ]}
      />
    </Card>
  );

  const unassignedPanel = (
    <Card
      size="small"
      style={{ marginBottom: 12 }}
      styles={{ body: { padding: 0 } }}
      title={(
        <Space size={8}>
          <span>Suppliers with no owner</span>
          {unassigned && (
            <span style={{ fontSize: 11, fontWeight: 400, color: NEUTRAL }}>
              {Object.entries(unassigned.byEntity).map(([e, n]) => `${e} ${n}`).join(' · ') || 'none'}
            </span>
          )}
        </Space>
      )}
      extra={<Button size="small" icon={<ReloadOutlined />} onClick={loadUnassigned}>refresh</Button>}
    >
      <div style={{ padding: '8px 14px', fontSize: 12, color: INK_2, borderBottom: '1px solid #f0f0f0' }}>
        A bill routes to its supplier&apos;s owner, so until one is set that supplier&apos;s bills have
        nowhere to go. Ordered by how much they have actually been billed — the long tail of one-off
        vendors can wait.
        {(() => {
          const n = Object.values(unassigned?.coveredByAccountRule || {}).reduce((a, b) => a + b, 0);
          return n > 0 ? ` A further ${n} are left out because an account rule above already covers them.` : '';
        })()}
      </div>
      <Table
        size="small"
        rowKey="id"
        dataSource={unassigned?.suppliers || []}
        loading={!unassigned}
        pagination={{ defaultPageSize: 15, showSizeChanger: true, pageSizeOptions: ['15', '50', '200'] }}
        locale={{ emptyText: 'Every active supplier has an owner' }}
        columns={[
          {
            title: 'Supplier',
            dataIndex: 'name',
            width: 260,
            render: (v, r) => (
              <span>
                {v}
                {r.waiting && (
                  <Tooltip title="A bill from this supplier is in the inbox and cannot be approved until it has an owner">
                    <Tag color="red" style={{ marginLeft: 6 }}>bill waiting</Tag>
                  </Tooltip>
                )}
              </span>
            ),
          },
          { title: 'Entity', dataIndex: 'entity', width: 140, render: (v) => entityTag(v) },
          {
            title: 'Bills', dataIndex: 'learnedFromBills', width: 80, align: 'right',
            render: (v) => <span style={{ fontVariantNumeric: 'tabular-nums', color: v > 20 ? INK : NEUTRAL }}>{v}</span>,
          },
          {
            title: 'Owner', width: 200,
            render: (_, r) => (
              <Select
                size="small" showSearch optionFilterProp="label" style={{ width: '100%' }}
                placeholder="choose"
                value={(assignDraft[r.id] || {}).ownerId}
                options={ownerOptions}
                onChange={(v) => setAssignDraft((d) => ({ ...d, [r.id]: { ...(d[r.id] || {}), ownerId: v } }))}
              />
            ),
          },
          {
            title: 'Account name', width: 260,
            render: (_, r) => {
              const draft = assignDraft[r.id] || {};
              const value = draft.accountName !== undefined ? draft.accountName : (r.learnedCostCentre || '');
              return (
                <Tooltip title={r.learnedCostCentre
                  ? `Learned from ${r.learnedFromBills} bills — change it if it is wrong`
                  : 'Nothing learned from its bills yet'}
                >
                  <div>
                    {accountPicker(
                      r.entity, value,
                      (_code, name) => setAssignDraft((d) => ({ ...d, [r.id]: { ...(d[r.id] || {}), accountName: name } })),
                      { byName: true, size: 'small' },
                    )}
                  </div>
                </Tooltip>
              );
            },
          },
          {
            title: '', width: 80,
            render: (_, r) => (
              <Button
                size="small" type="primary"
                style={{ background: '#54A2CC', borderColor: '#54A2CC' }}
                loading={assigning === r.id}
                disabled={!(assignDraft[r.id] || {}).ownerId}
                onClick={() => assignSupplier(r)}
              >
                assign
              </Button>
            ),
          },
        ]}
      />
    </Card>
  );

  const peopleTab = !people ? <Card size="small"><Spin /></Card> : (
    <Card size="small" styles={{ body: { padding: 0 } }}>
      <div style={{ padding: '10px 14px', borderBottom: '1px solid #f0f0f0' }}>
        <Space direction="vertical" size={2}>
          <span style={{ fontSize: 12, color: INK_2 }}>
            Bill approval is not here — it follows supplier ownership, shown as a count per entity.
          </span>
          <Space size={12} wrap>
            {RIGHTS.map((r) => (
              <span key={r.key} style={{ fontSize: 11, color: NEUTRAL }}>
                <b style={{ color: INK_2 }}>{r.label}</b> — {r.hint}
              </span>
            ))}
          </Space>
        </Space>
      </div>
      <Table
        size="small"
        rowKey="id"
        dataSource={people.people}
        scroll={{ x: 260 + people.entities.length * 200 }}
        pagination={false}
        columns={[
          {
            title: 'Person', dataIndex: 'name', fixed: 'left', width: 240,
            render: (v, r) => (
              <div>
                <div style={{ color: INK }}>{v}</div>
                <div style={{ fontSize: 11, color: NEUTRAL }}>{r.role || 'no role'} · {r.email}</div>
              </div>
            ),
          },
          ...people.entities.map((e) => ({
            title: e.name,
            width: 200,
            render: (_, person) => {
              const owned = person.suppliersOwned[e.code] || 0;
              return (
                <Space direction="vertical" size={2}>
                  <Space size={4}>
                    {RIGHTS.map((r) => {
                      const on = !!(person.rights[e.code] || {})[r.key];
                      const tag = `${person.id}:${e.code}:${r.key}`;
                      return (
                        <Tooltip key={r.key} title={r.hint}>
                          <Button
                            size="small"
                            type={on ? 'primary' : 'default'}
                            loading={savingRight === tag}
                            style={on ? { background: '#54A2CC', borderColor: '#54A2CC' } : undefined}
                            onClick={() => toggleRight(person, e.code, r.key)}
                          >
                            {r.label}
                          </Button>
                        </Tooltip>
                      );
                    })}
                  </Space>
                  {owned > 0 && (
                    <span style={{ fontSize: 11, color: NEUTRAL }}>
                      owns {owned} supplier{owned === 1 ? '' : 's'}
                    </span>
                  )}
                </Space>
              );
            },
          })),
        ]}
      />
    </Card>
  );

  if (!access && loading) {
    return (
      <PageLayout>
        <div style={{ padding: 60, textAlign: 'center' }}><Spin /></div>
      </PageLayout>
    );
  }

  const noEntities = access && access.entities.length === 0;


  /**
   * One budget line, open for revision.
   *
   * Saving supersedes rather than overwrites, and the drawer says so — somebody
   * changing a figure should know the previous one remains on the record rather
   * than discovering it later.
   */
  const editDrawer = (
    <Drawer
      title={editRow ? `${editRow.name} — ${month.format('MMMM YYYY')}` : ''}
      width={400}
      open={!!editRow}
      onClose={() => setEditRow(null)}
      extra={(
        <Button size="small" danger loading={savingLine} onClick={removeLine}>
          remove
        </Button>
      )}
      footer={(
        <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
          <Button onClick={() => setEditRow(null)}>Cancel</Button>
          <Button
            type="primary" loading={savingLine} onClick={saveLine}
            style={{ background: '#54A2CC', borderColor: '#54A2CC' }}
          >
            Save
          </Button>
        </Space>
      )}
    >
      {editRow && (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <div style={{ fontSize: 12, color: INK_2 }}>
            {editRow.dimension === 'account'
              ? 'An expense account. On an envelope account this figure is the whole monthly commitment; otherwise it caps the supplier lines inside it.'
              : 'A supplier line. Its bills for this month are checked against this figure.'}
          </div>

          <div>
            <div style={{ fontSize: 12, color: INK_2, marginBottom: 4 }}>Amount (AUD)</div>
            <InputNumber
              style={{ width: '100%' }} min={0} step={100} autoFocus
              value={editAmount}
              onChange={setEditAmount}
              formatter={(v) => `$ ${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={(v) => v.replace(/\$\s?|(,*)/g, '')}
            />
            <div style={{ fontSize: 11, color: NEUTRAL, marginTop: 4 }}>
              {fmtMoney(editRow.actual)} spent so far
              {editRow.budget != null && editRow.actual > editRow.budget && (
                <span style={{ color: OVER }}> · already past the current figure</span>
              )}
            </div>
          </div>

          <div>
            <div style={{ fontSize: 12, color: INK_2, marginBottom: 4 }}>Expense account</div>
            {accountPicker(
              entity,
              editAccount.code,
              (code, name) => setEditAccount({ code: code || '', name: name || '' }),
              { disabled: editRow.dimension === 'account' },
            )}
            {editRow.dimension === 'account' && (
              <div style={{ fontSize: 11, color: NEUTRAL, marginTop: 4 }}>
                Fixed — this line <i>is</i> the account.
              </div>
            )}
          </div>

          <div>
            <div style={{ fontSize: 12, color: INK_2, marginBottom: 4 }}>Note</div>
            <Input.TextArea
              rows={3} maxLength={500} showCount
              value={editNote}
              placeholder="Why this figure"
              onChange={(e) => setEditNote(e.target.value)}
            />
          </div>

          <Alert
            type="info"
            message={(
              <span style={{ fontSize: 12 }}>
                Saving replaces this line rather than editing it in place. The old figure stays on
                the record, and both changes are attributed to you.
              </span>
            )}
          />
        </Space>
      )}
    </Drawer>
  );

  /**
   * Approving is also a cash-flow instruction, so it asks when.
   *
   * Defaulted to the bill's due date because that is what Finance would schedule
   * anyway; changing it is a deliberate decision about when the money leaves,
   * and the trail records whichever was chosen.
   */
  const approveModal = (
    <Modal
      title={approving ? `Approve ${approving.row.supplierName}` : ''}
      open={!!approving}
      onCancel={() => setApproving(null)}
      okText="Approve"
      confirmLoading={deciding === approving?.row?.reviewId}
      okButtonProps={{ disabled: !approving?.date, style: { background: UNDER, borderColor: UNDER } }}
      onOk={() => decide(approving.row, 'approved', {
        plannedPaymentDate: approving.date.format('YYYY-MM-DD'),
      })}
    >
      {approving && (
        <Space direction="vertical" size={14} style={{ width: '100%' }}>
          <div style={{ fontSize: 13, color: INK_2 }}>
            {fmtMoney(approving.row.opexAmount)} opex
            {approving.row.costCentre ? ` · ${approving.row.costCentre}` : ''}
            {approving.row.invoiceNumber ? ` · ${approving.row.invoiceNumber}` : ''}
          </div>

          <div>
            <div style={{ fontSize: 12, color: INK_2, marginBottom: 4 }}>Planned payment date</div>
            <DatePicker
              style={{ width: '100%' }}
              value={approving.date}
              allowClear={false}
              onChange={(v) => v && setApproving((a) => ({ ...a, date: v }))}
            />
            <div style={{ fontSize: 11, color: NEUTRAL, marginTop: 4 }}>
              {approving.row.dueDate
                ? `Due ${dayjs(approving.row.dueDate).format('D MMM YYYY')}`
                : 'This bill carries no due date, so one has to be chosen'}
              {approving.row.dueDate && !approving.date.isSame(dayjs(approving.row.dueDate), 'day') && (
                <span style={{ color: OVER }}> · not the due date</span>
              )}
            </div>
          </div>

          {approving.row.check.needsExecutive && (
            <Alert
              type="warning"
              showIcon
              message={(
                <span style={{ fontSize: 12 }}>
                  {approving.row.check.detail}. Approving sends this to the executive rather than
                  finishing it.
                </span>
              )}
            />
          )}
        </Space>
      )}
    </Modal>
  );

  /**
   * One supplier, open for management.
   *
   * Ownership decides who approves that supplier's payments, so the actions here
   * need the right that governs other people's authority — the same gate as
   * People & access. Everyone with entity access can still read the list,
   * because knowing where a bill will go is not a privilege.
   */
  const supplierDrawer = (
    <Drawer
      title={supRow ? supRow.name : ''}
      width={420}
      open={!!supRow}
      onClose={() => setSupRow(null)}
      footer={access?.canManageAccess && (
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <Button
            danger
            onClick={() => confirmByTyping({
              title: `Remove ${supRow.name} from budgeting?`,
              body: (
                <>
                  It stops appearing in this module and its owner is cleared. The Xero contact is
                  untouched, and its budget history and reviewed bills stay readable.
                  {supRow.learnedFromBills > 0 && (
                    <div style={{ marginTop: 6, color: OVER }}>
                      It has {supRow.learnedFromBills} bills behind it. New bills from it will
                      arrive with no owner.
                    </div>
                  )}
                </>
              ),
              onConfirm: async () => {
                try {
                  await api.delete(`/api/budget/suppliers/${supRow.id}`);
                  message.success(`${supRow.name} removed from budgeting`);
                  setSupRow(null);
                  loadEntity();
                  loadUnassigned();
                } catch (err) {
                  message.error(err.response?.data?.message || 'Could not remove the supplier');
                }
              },
            })}
          >
            remove supplier
          </Button>
          <Space>
            <Button onClick={() => setSupRow(null)}>Cancel</Button>
            <Button
              type="primary" loading={savingSup}
              style={{ background: '#54A2CC', borderColor: '#54A2CC' }}
              onClick={async () => {
                setSavingSup(true);
                try {
                  await api.put(`/api/budget/suppliers/${supRow.id}/owner`, {
                    ownerId: supDraft.ownerId || null,
                    backupId: supDraft.backupId || null,
                    accountName: supDraft.accountName ?? null,
                  });
                  message.success(`${supRow.name} updated`);
                  setSupRow(null);
                  loadEntity();
                  loadUnassigned();
                } catch (err) {
                  message.error(err.response?.data?.message || 'Could not save');
                }
                setSavingSup(false);
              }}
            >
              Save
            </Button>
          </Space>
        </Space>
      )}
    >
      {supRow && (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <Space size={6} wrap style={{ fontSize: 12, color: INK_2 }}>
            <Tag>{supRow.entity}</Tag>
            <span>{supRow.learnedFromBills} bills</span>
            {supRow.isStaff && <Tag color="gold">staff — reimbursements</Tag>}
            {!supRow.active && <Tag>dormant in Xero</Tag>}
            {supRow.source === 'group' && <Tag color="green">declared in Xero group</Tag>}
          </Space>

          {!access?.canManageAccess && (
            <Alert
              type="info"
              message={(
                <span style={{ fontSize: 12 }}>
                  Read-only. Changing who approves a supplier&apos;s payments needs the entity
                  access right.
                </span>
              )}
            />
          )}

          <div>
            <div style={{ fontSize: 12, color: INK_2, marginBottom: 4 }}>Owner — approves its bills</div>
            <Select
              showSearch optionFilterProp="label" style={{ width: '100%' }}
              placeholder="nobody — its bills have nowhere to go"
              allowClear
              disabled={!access?.canManageAccess}
              value={supDraft.ownerId || undefined}
              options={ownerOptions}
              onChange={(v) => setSupDraft((d) => ({ ...d, ownerId: v || null }))}
            />
          </div>

          <div>
            <div style={{ fontSize: 12, color: INK_2, marginBottom: 4 }}>Backup approver</div>
            <Select
              showSearch optionFilterProp="label" style={{ width: '100%' }}
              placeholder="none"
              allowClear
              disabled={!access?.canManageAccess}
              value={supDraft.backupId || undefined}
              options={ownerOptions.filter((o) => o.value !== supDraft.ownerId)}
              onChange={(v) => setSupDraft((d) => ({ ...d, backupId: v || null }))}
            />
          </div>

          <div>
            <div style={{ fontSize: 12, color: INK_2, marginBottom: 4 }}>Default account</div>
            {accountPicker(
              supRow.entity,
              supDraft.accountName ?? '',
              (_code, name) => setSupDraft((d) => ({ ...d, accountName: name })),
              { byName: true, disabled: !access?.canManageAccess },
            )}
            <div style={{ fontSize: 11, color: NEUTRAL, marginTop: 4 }}>
              {supRow.learnedCostCentre
                ? `Learned from its bills: ${supRow.learnedCostCentre}`
                : 'Nothing learned from its bills yet'}
            </div>
          </div>
        </Space>
      )}
    </Drawer>
  );

  /**
   * What one person has approved, and how it sits against this month.
   *
   * Approvals rather than spend: a person with approval authority is
   * accountable for what they signed, which is a different question from what
   * the entity spent. Six months is enough to show a rhythm and short enough
   * that the figures still describe how things work now.
   */
  /**
   * The approval history, cut however finance asks for it.
   *
   * One shape before — one person, one entity, split by supplier and account.
   * The dimensions are now chosen: any combination of month, entity, supplier,
   * account and approver, with the server aggregating over whichever were
   * named.
   */
  const reportTab = (() => {
    const DIMS = [
      { value: 'billMonth', label: 'Month submitted' },
      { value: 'approvedMonth', label: 'Month approved' },
      { value: 'entity', label: 'Entity' },
      { value: 'supplier', label: 'Supplier' },
      { value: 'account', label: 'Account' },
      { value: 'person', label: 'Approver' },
    ];
    const labelOf = (v) => (DIMS.find((d) => d.value === v) || {}).label || v;
    const opts = report?.options || {};

    const cols = [
      // One column per chosen dimension, in the order they were chosen: the
      // order is the grouping, so the table has to show it.
      ...(report?.groupBy || []).map((dim, i) => ({
        title: labelOf(dim),
        dataIndex: ['cells', dim],
        width: dim.endsWith('Month') ? 120 : 200,
        fixed: i === 0 ? 'left' : undefined,
        render: (v) => (dim.endsWith('Month') && v
          ? dayjs(v).format('MMM YYYY')
          : (v || <span style={{ color: NEUTRAL }}>—</span>)),
      })),
      {
        title: 'Approved', dataIndex: 'total', width: 130, align: 'right',
        defaultSortOrder: 'descend',
        sorter: (a, b) => a.total - b.total,
        render: (v) => <span style={{ fontVariantNumeric: 'tabular-nums', color: INK }}>{fmtMoney(v)}</span>,
      },
      {
        title: 'Bills', dataIndex: 'bills', width: 80, align: 'right',
        sorter: (a, b) => a.bills - b.bills,
        render: (v) => <span style={{ fontVariantNumeric: 'tabular-nums', color: INK_2 }}>{v}</span>,
      },
      ...(report ? report.months.map((m) => ({
        title: dayjs(m).format('MMM'),
        width: 100,
        align: 'right',
        render: (_, r) => {
          const v = r.byMonth[m];
          return v
            ? <span style={{ fontVariantNumeric: 'tabular-nums', color: INK_2 }}>{fmtMoney(v)}</span>
            : <span style={{ color: '#d0d7e0' }}>—</span>;
        },
      })) : []),
    ];

    const picker = (label, width, value, onChange, options, placeholder) => (
      <div>
        <div style={{ fontSize: 11, color: NEUTRAL, marginBottom: 2 }}>{label}</div>
        <Select
          size="small" mode="multiple" allowClear showSearch optionFilterProp="label"
          style={{ width }} maxTagCount="responsive"
          placeholder={placeholder}
          value={value} onChange={onChange} options={options}
        />
      </div>
    );

    return (
      <>
        <Card size="small" style={{ marginBottom: 12 }} styles={{ body: { padding: '10px 14px' } }}>
          <Space wrap size="middle" align="end">
            <div>
              <div style={{ fontSize: 11, color: NEUTRAL, marginBottom: 2 }}>Group by</div>
              <Select
                size="small" mode="multiple" allowClear={false} style={{ width: 320 }}
                value={reportGroupBy}
                onChange={(v) => setReportGroupBy(v.length ? v : ['supplier'])}
                options={DIMS}
                maxTagCount="responsive"
              />
            </div>
            {picker('Entities', 200, reportFilters.entities,
              (v) => setReportFilters((f) => ({ ...f, entities: v })),
              opts.entities?.map((e) => ({ value: e.key, label: e.label })) || [], 'all I can see')}
            {picker('Suppliers', 220, reportFilters.suppliers,
              (v) => setReportFilters((f) => ({ ...f, suppliers: v })),
              opts.suppliers?.map((e) => ({ value: e.key, label: e.label })) || [], 'any')}
            {picker('Accounts', 200, reportFilters.accounts,
              (v) => setReportFilters((f) => ({ ...f, accounts: v })),
              opts.accounts?.map((e) => ({ value: e.key, label: e.label })) || [], 'any')}
            {access?.canManageAccess && picker('Approvers', 220, reportFilters.people,
              (v) => setReportFilters((f) => ({ ...f, people: v })),
              opts.people?.map((e) => ({ value: e.key, label: e.label })) || [], 'me')}
            <Button
              size="small" type="primary" icon={<ReloadOutlined />} loading={reportLoading}
              style={{ background: '#54A2CC', borderColor: '#54A2CC' }}
              onClick={loadReport}
            >
              {report ? 'refresh' : 'build report'}
            </Button>
          </Space>
        </Card>

        {!report ? (
          <Card size="small">
            <Empty description={reportLoading ? 'Reading the approval trail…' : 'Build the report to see approvals'} />
          </Card>
        ) : (
          <>
            <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
              {[
                {
                  t: `Approved over ${report.months.length} months`,
                  v: fmtMoney(report.totals.approved),
                  sub: `${report.totals.bills} bill${report.totals.bills === 1 ? '' : 's'}`,
                },
                { t: 'Groups', v: String(report.rows.length), sub: report.groupBy.map(labelOf).join(' · ') },
                {
                  t: 'Entities',
                  v: String(report.entities.length),
                  sub: report.entities.map((e) => (opts.entities || []).find((x) => x.key === e)?.label || e).join(', '),
                },
                {
                  t: 'Window',
                  v: dayjs(report.months[report.months.length - 1]).format('MMM YY'),
                  sub: `from ${dayjs(report.months[0]).format('MMM YYYY')}`,
                },
              ].map((k) => (
                <Col xs={24} sm={12} lg={6} key={k.t}>
                  <Card size="small" styles={{ body: { padding: '12px 14px' } }}>
                    <Statistic
                      title={<span style={{ fontSize: 12, color: INK_2 }}>{k.t}</span>}
                      value={k.v}
                      valueStyle={{ color: INK, fontSize: 22, fontVariantNumeric: 'tabular-nums' }}
                    />
                    {k.sub && <div style={{ fontSize: 11, color: NEUTRAL }}>{k.sub}</div>}
                  </Card>
                </Col>
              ))}
            </Row>

            {/* This changes what the total means, so it is stated rather than
                left for somebody to discover by adding the column up. */}
            {report.countedPerApprover && (
              <Alert
                type="info" showIcon style={{ marginBottom: 12 }}
                message={(
                  <span style={{ fontSize: 12 }}>
                    Grouped by approver, so a bill that went to the executive is counted under
                    each signature — both people did approve it. The total is therefore higher
                    than the money that left. Remove <b>Approver</b> to count each bill once.
                  </span>
                )}
              />
            )}

            <Card size="small">
              <Table
                size="small"
                rowKey="key"
                dataSource={report.rows}
                columns={cols}
                pagination={{ defaultPageSize: 25, showSizeChanger: true }}
                scroll={{ x: 700 + report.months.length * 100 }}
                locale={{ emptyText: 'No approvals match these filters' }}
                summary={(pageRows) => {
                  const t = pageRows.reduce((a, r) => a + r.total, 0);
                  const b = pageRows.reduce((a, r) => a + r.bills, 0);
                  return (
                    <Table.Summary.Row style={{ background: '#fafcfd' }}>
                      <Table.Summary.Cell index={0} colSpan={report.groupBy.length}>
                        <b>This page</b>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={1} align="right">
                        <b style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(t)}</b>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={2} align="right">
                        <b style={{ fontVariantNumeric: 'tabular-nums' }}>{b}</b>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  );
                }}
              />
            </Card>
          </>
        )}
      </>
    );
  })();

  /**
   * One of my suppliers, and the one thing its owner can do without anybody
   * else: hand it on.
   */
  const mySupplierDrawer = (
    <Drawer
      title={mySup ? mySup.name : ''}
      width={420}
      open={!!mySup}
      onClose={() => { setMySup(null); setAppointTo(null); }}
    >
      {mySup && (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <Space size={6} wrap style={{ fontSize: 12, color: INK_2 }}>
            <Tag>{mySup.entity}</Tag>
            {mySup.isOwner ? <Tag color="blue">you own it</Tag> : <Tag>you are the backup</Tag>}
            {mySup.isStaff && <Tag color="gold">staff — reimbursements</Tag>}
            <span>{mySup.learnedFromBills || 0} bills</span>
          </Space>

          <div style={{ fontSize: 13, color: INK_2 }}>
            Coded to <b>{mySup.costCentre || mySup.learnedCostCentre || 'nothing yet'}</b>
          </div>

          {mySup.isOwner ? (
            <>
              {/* The owner's two levers: who covers for them, and who takes over. */}
              <div>
                <div style={{ fontSize: 12, color: INK_2, marginBottom: 4 }}>Backup approver</div>
                <Select
                  showSearch optionFilterProp="label" allowClear style={{ width: '100%' }}
                  placeholder="Nobody — its bills wait for you alone"
                  value={supBackup}
                  options={everyoneOptions.filter((o) => o.value !== mySup.ownerId)}
                  onChange={(v) => setSupBackup(v || null)}
                />
                <div style={{ fontSize: 11, color: NEUTRAL, marginTop: 4 }}>
                  They can approve this supplier&apos;s bills when you cannot. You stay the owner.
                </div>
                {supBackup !== (mySup.backupId || null) && (
                  <Button
                    size="small" type="primary" loading={savingBackup}
                    style={{ marginTop: 8, background: '#54A2CC', borderColor: '#54A2CC' }}
                    onClick={saveBackup}
                  >
                    {supBackup ? 'Save backup' : 'Clear backup'}
                  </Button>
                )}
              </div>

              <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 14 }}>
                <div style={{ fontSize: 12, color: INK_2, marginBottom: 4 }}>Hand this supplier to</div>
                <Select
                  showSearch optionFilterProp="label" style={{ width: '100%' }}
                  placeholder="Search by name or email"
                  value={appointTo}
                  options={everyoneOptions.filter((o) => o.value !== mySup.ownerId)}
                  onChange={setAppointTo}
                />

                <Alert
                  type="warning"
                  showIcon
                  style={{ marginTop: 10 }}
                  message={(
                    <span style={{ fontSize: 12 }}>
                      They become the owner. Every bill from {mySup.name} routes to them from now
                      on, and raising its budget becomes theirs too. You keep no claim on it — ask
                      them if you need it back. The handover is recorded against the supplier.
                    </span>
                  )}
                />

                <Button
                  type="primary" block loading={appointing}
                  disabled={!appointTo}
                  style={{ marginTop: 10, ...(appointTo ? { background: '#54A2CC', borderColor: '#54A2CC' } : {}) }}
                  onClick={appoint}
                >
                  Appoint
                </Button>
              </div>
            </>
          ) : (
            <>
              {/* A backup can see who to chase, and nothing else. Ownership is
                  what routes payments, so changing it is the owner's call. */}
              <div>
                <div style={{ fontSize: 12, color: INK_2, marginBottom: 4 }}>Owner</div>
                <div style={{ fontSize: 15, color: INK }}>{mySup.ownerName || 'nobody'}</div>
              </div>
              <Alert
                type="info"
                message={(
                  <span style={{ fontSize: 12 }}>
                    You approve this supplier&apos;s bills when {mySup.ownerName || 'the owner'}{' '}
                    cannot. Changing the backup, or handing the supplier on, is the owner&apos;s to
                    do — ask {mySup.ownerName || 'them'} if it needs to move.
                  </span>
                )}
              />
            </>
          )}
        </Space>
      )}
    </Drawer>
  );

  /**
   * One of my budget lines: what it is, what has happened to it, and — while
   * its month is open — what can still be changed.
   *
   * The supplier and the account are fixed. Those two are the line's identity:
   * change either and it is a different commitment, which is what raising a new
   * line is for. Everything else is a figure or a timing, and both move.
   */
  const myLineDrawer = (
    <Drawer
      title={myLine ? myLine.name : ''}
      width={440}
      open={!!myLine}
      onClose={() => setMyLine(null)}
      footer={myLine && !myLine.monthLocked && (
        <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
          <Button onClick={() => setMyLine(null)}>Cancel</Button>
          <Button
            type="primary" loading={savingMyLine} onClick={saveMyLine}
            style={{ background: '#54A2CC', borderColor: '#54A2CC' }}
          >
            Save
          </Button>
        </Space>
      )}
    >
      {myLine && (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          {/* ---- what it is, and what has happened to it ---- */}
          <div style={{ background: '#f7f9fb', borderRadius: 8, padding: '12px 14px' }}>
            <Space size={6} wrap style={{ marginBottom: 10 }}>
              <Tag>{myLine.entity}</Tag>
              <Tag>{myLine.dimension === 'account' ? 'account envelope' : 'supplier line'}</Tag>
              <Tag color={myLine.status === 'granted' ? 'green' : 'gold'}>{myLine.status}</Tag>
              {myLine.origin === 'estimate' && <Tag>estimated</Tag>}
              {myLine.monthLocked
                ? <Tag color="blue" icon={<LockOutlined />}>month locked</Tag>
                : <Tag icon={<UnlockOutlined />}>month open</Tag>}
            </Space>

            <Row gutter={[10, 8]}>
              <Col span={12}>
                <div style={{ fontSize: 11, color: NEUTRAL }}>Account</div>
                <div style={{ fontSize: 13, color: INK }}>
                  {myLine.accountName || myLine.accountCode
                    || <span style={{ color: NEUTRAL }}>not set</span>}
                </div>
                {myLine.accountName && myLine.accountCode && (
                  <div style={{ fontSize: 11, color: NEUTRAL }}>{myLine.accountCode}</div>
                )}
              </Col>
              <Col span={12}>
                <div style={{ fontSize: 11, color: NEUTRAL }}>Approved against it</div>
                <div style={{ fontSize: 13, color: myLine.approvedAmount > myLine.requested ? OVER : INK }}>
                  {fmtMoney(myLine.approvedAmount)}
                  <span style={{ color: NEUTRAL, fontSize: 11 }}>
                    {' '}· {myLine.approvedBills} bill{myLine.approvedBills === 1 ? '' : 's'}
                  </span>
                </div>
              </Col>
              <Col span={12}>
                <div style={{ fontSize: 11, color: NEUTRAL }}>Last changed</div>
                <div style={{ fontSize: 13, color: INK }}>
                  {dayjs(myLine.updatedAt).format('D MMM YYYY HH:mm')}
                </div>
              </Col>
              <Col span={12}>
                <div style={{ fontSize: 11, color: NEUTRAL }}>By</div>
                <div style={{ fontSize: 13, color: INK }}>{myLine.updatedByName || '—'}</div>
              </Col>
              {myLine.monthLocked && myLine.lockedAt && (
                <Col span={24}>
                  <div style={{ fontSize: 11, color: NEUTRAL }}>Month locked</div>
                  <div style={{ fontSize: 13, color: INK }}>
                    {dayjs(myLine.lockedAt).format('D MMM YYYY HH:mm')}
                  </div>
                </Col>
              )}
            </Row>
          </div>

          {myLine.monthLocked ? (
            <Alert
              type="info"
              showIcon
              message={(
                <span style={{ fontSize: 12 }}>
                  {dayjs(myLine.month).format('MMMM YYYY')} has been signed off, so this line is
                  settled at {fmtMoney(myLine.granted ?? myLine.requested)} and cannot be changed.
                  Reopening a month takes two of the people who can lock it.
                </span>
              )}
            />
          ) : (
            <>
              <div>
                <div style={{ fontSize: 12, color: INK_2, marginBottom: 4 }}>Amount (AUD)</div>
                <InputNumber
                  style={{ width: '100%' }} min={0} step={100} autoFocus
                  value={myLineAmount}
                  onChange={setMyLineAmount}
                  formatter={(v) => `$ ${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={(v) => v.replace(/\$\s?|(,*)/g, '')}
                />
                <div style={{ fontSize: 11, color: NEUTRAL, marginTop: 4 }}>
                  Raised at {fmtMoney(myLine.requested)}
                  {myLine.approvedAmount > 0 && ` · ${fmtMoney(myLine.approvedAmount)} already approved against it`}
                </div>
              </div>

              <div>
                <div style={{ fontSize: 12, color: INK_2, marginBottom: 4 }}>Month</div>
                <DatePicker
                  picker="month" style={{ width: '100%' }} allowClear={false}
                  value={myLineMonth}
                  onChange={(v) => v && setMyLineMonth(v.startOf('month'))}
                />
                {myLineMonth && !myLineMonth.isSame(dayjs(myLine.month), 'month') && (
                  <div style={{ fontSize: 11, color: OVER, marginTop: 4 }}>
                    Moving out of {dayjs(myLine.month).format('MMM YYYY')}. The destination has to be
                    open too, and any line already there for this{' '}
                    {myLine.dimension === 'account' ? 'account' : 'supplier'} is replaced.
                  </div>
                )}
              </div>

              <div>
                <div style={{ fontSize: 12, color: INK_2, marginBottom: 4 }}>Expense account</div>
                {accountPicker(
                  myLine.entity,
                  myLineAccount.code,
                  (code, name) => setMyLineAccount({ code: code || '', name: name || '' }),
                )}
                <div style={{ fontSize: 11, color: NEUTRAL, marginTop: 4 }}>
                  Which category this budget belongs to. It decides where the line rolls up in the
                  entity report, and a line with no account cannot be attributed to one.
                </div>
                {String(myLineAccount.code || '') !== String(myLine.accountCode || '') && (
                  <div style={{ fontSize: 11, color: OVER, marginTop: 4 }}>
                    Moving off {myLine.accountName || 'no account'}. Any line already held for this
                    supplier on the new account is replaced.
                  </div>
                )}
              </div>

              <div>
                <div style={{ fontSize: 12, color: INK_2, marginBottom: 4 }}>Frequency</div>
                <Select
                  style={{ width: '100%' }}
                  value={myLineFreq}
                  options={[
                    { value: 'one_off', label: 'One-off — that month only' },
                    { value: 'monthly', label: 'Monthly' },
                    { value: 'quarterly', label: 'Quarterly' },
                    { value: 'annual', label: 'Annual' },
                  ]}
                  onChange={setMyLineFreq}
                />
                <div style={{ fontSize: 11, color: NEUTRAL, marginTop: 4 }}>
                  How this cost recurs. Changing it here relabels this line; it does not create the
                  other months.
                </div>
              </div>

              <div>
                <div style={{ fontSize: 12, color: INK_2, marginBottom: 4 }}>Note</div>
                <Input.TextArea
                  rows={3} maxLength={500} showCount
                  value={myLineNote}
                  placeholder="Why this figure"
                  onChange={(e) => setMyLineNote(e.target.value)}
                />
              </div>

              <Alert
                type="info"
                message={(
                  <span style={{ fontSize: 12 }}>
                    The supplier is fixed — a budget for a different supplier is a different
                    commitment, which is what a new line is for. Saving replaces this line rather
                    than editing it, so the old figure stays on the record.
                    {myLine.origin === 'estimate' && ' It also becomes yours: the next estimate will leave it alone.'}
                  </span>
                )}
              />
            </>
          )}
        </Space>
      )}
    </Drawer>
  );

  /**
   * Raising a budget from My Day.
   *
   * Scoped to a supplier of mine, which is the whole point of raising one here:
   * the entity page is where somebody with entity-wide rights works, and this is
   * where the person who answers for a supplier records what it will cost. A
   * locked month is refused in the picker rather than on submit, so nobody fills
   * in a form the server is going to reject.
   */
  const newBudgetDrawer = (() => {
    const mySups = (dashboard?.suppliers || []).filter((x) => x.isOwner || !x.isOwner);
    const entities = [...new Set(mySups.map((x) => x.entity))].sort();
    const inEntity = mySups.filter((x) => x.entity === newLine.entity);
    const monthLocked = isLockedMonth(newLine.entity, newLine.month);
    return (
      <Drawer
        title="New budget line"
        width={420}
        open={newOpen}
        onClose={() => setNewOpen(false)}
        footer={(
          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button onClick={() => setNewOpen(false)}>Cancel</Button>
            <Button
              type="primary" loading={savingNew} onClick={submitNewLine}
              disabled={monthLocked}
              style={monthLocked ? undefined : { background: '#54A2CC', borderColor: '#54A2CC' }}
            >
              Raise
            </Button>
          </Space>
        )}
      >
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <div>
            <div style={{ fontSize: 12, color: INK_2, marginBottom: 4 }}>Entity</div>
            <Select
              style={{ width: '100%' }}
              value={newLine.entity}
              options={entities.map((e) => ({ value: e, label: e }))}
              onChange={(v) => {
                setNewLine((x) => ({ ...x, entity: v, supplierId: null, accountCode: '', accountName: '' }));
                ensureAccounts(v);
              }}
            />
          </div>

          <div>
            <div style={{ fontSize: 12, color: INK_2, marginBottom: 4 }}>Supplier</div>
            <Select
              showSearch optionFilterProp="label" style={{ width: '100%' }}
              placeholder={inEntity.length ? 'Search your suppliers' : 'You hold none in this entity'}
              value={newLine.supplierId}
              options={inEntity.map((x) => ({ value: x.id, label: x.name }))}
              onChange={(v) => {
                const sup = inEntity.find((x) => x.id === v);
                // Seed the account from what its bills are usually coded to —
                // right most of the time, and visible so a wrong one is obvious.
                setNewLine((x) => ({
                  ...x,
                  supplierId: v,
                  accountName: x.accountName || (sup && (sup.costCentre || sup.learnedCostCentre)) || '',
                }));
              }}
            />
            <div style={{ fontSize: 11, color: NEUTRAL, marginTop: 4 }}>
              Only suppliers you own or back up. An account-wide envelope is raised from the entity
              page.
            </div>
          </div>

          <div>
            <div style={{ fontSize: 12, color: INK_2, marginBottom: 4 }}>Expense account</div>
            {accountPicker(
              newLine.entity,
              newLine.accountCode,
              (code, name) => setNewLine((x) => ({ ...x, accountCode: code || '', accountName: name || '' })),
            )}
          </div>

          <div>
            <div style={{ fontSize: 12, color: INK_2, marginBottom: 4 }}>Amount (AUD)</div>
            <InputNumber
              style={{ width: '100%' }} min={0} step={100}
              value={newLine.amount}
              onChange={(v) => setNewLine((x) => ({ ...x, amount: v }))}
              formatter={(v) => `$ ${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={(v) => v.replace(/\$\s?|(,*)/g, '')}
            />
          </div>

          <div>
            <div style={{ fontSize: 12, color: INK_2, marginBottom: 4 }}>Month</div>
            <DatePicker
              picker="month" style={{ width: '100%' }} allowClear={false}
              value={newLine.month}
              // A signed month is not a thing to offer and then refuse.
              disabledDate={(d) => isLockedMonth(newLine.entity, d)}
              onChange={(v) => v && setNewLine((x) => ({ ...x, month: v.startOf('month') }))}
            />
            {monthLocked && (
              <div style={{ fontSize: 11, color: OVER, marginTop: 4 }}>
                {newLine.month.format('MMMM YYYY')} is locked. Nothing can be added to a month that
                has been signed off.
              </div>
            )}
          </div>

          <div>
            <div style={{ fontSize: 12, color: INK_2, marginBottom: 4 }}>Frequency</div>
            <Select
              style={{ width: '100%' }}
              value={newLine.frequency}
              options={[
                { value: 'one_off', label: 'One-off — that month only' },
                { value: 'monthly', label: 'Monthly' },
                { value: 'quarterly', label: 'Quarterly' },
                { value: 'annual', label: 'Annual' },
              ]}
              onChange={(v) => setNewLine((x) => ({ ...x, frequency: v, months: v === 'one_off' ? 1 : x.months }))}
            />
          </div>

          {newLine.frequency !== 'one_off' && (
            <div>
              <div style={{ fontSize: 12, color: INK_2, marginBottom: 4 }}>How many</div>
              <InputNumber
                style={{ width: '100%' }} min={1} max={12}
                value={newLine.months}
                onChange={(v) => setNewLine((x) => ({ ...x, months: v || 1 }))}
              />
              <div style={{ fontSize: 11, color: NEUTRAL, marginTop: 4 }}>
                One line is written per occurrence, up to twelve months out. If any of them lands in
                a locked month the whole request is refused rather than half-written.
              </div>
            </div>
          )}

          <div>
            <div style={{ fontSize: 12, color: INK_2, marginBottom: 4 }}>Note</div>
            <Input.TextArea
              rows={3} maxLength={500} showCount
              value={newLine.note}
              placeholder="Why this figure"
              onChange={(e) => setNewLine((x) => ({ ...x, note: e.target.value }))}
            />
          </div>
        </Space>
      </Drawer>
    );
  })();

  /**
   * Handing one bill to somebody else.
   *
   * Deliberately not the same act as handing over the supplier: this bill moves
   * and the supplier's ownership does not, which is what somebody wants when a
   * single invoice needs a different pair of eyes. Changing who owns the
   * supplier is on the My suppliers tab and changes every bill after it.
   */
  const handOnModal = (
    <Modal
      title={handing ? `Hand on ${handing.row.supplierName}` : ''}
      open={!!handing}
      onCancel={() => setHanding(null)}
      okText="Hand on"
      confirmLoading={deciding === handing?.row?.reviewId}
      okButtonProps={{ disabled: !handing?.toUserId }}
      onOk={() => decide(handing.row, 'reassigned', { assignTo: handing.toUserId })}
    >
      {handing && (
        <Space direction="vertical" size={14} style={{ width: '100%' }}>
          <div style={{ fontSize: 13, color: INK_2 }}>
            {fmtMoney(handing.row.opexAmount)} opex
            {handing.row.costCentre ? ` · ${handing.row.costCentre}` : ''}
            {handing.row.invoiceNumber ? ` · ${handing.row.invoiceNumber}` : ''}
          </div>
          <div>
            <div style={{ fontSize: 12, color: INK_2, marginBottom: 4 }}>Give it to</div>
            <Select
              showSearch optionFilterProp="label" style={{ width: '100%' }}
              placeholder="Search by name or email"
              value={handing.toUserId}
              options={everyoneOptions}
              onChange={(v) => setHanding((h) => ({ ...h, toUserId: v }))}
            />
          </div>
          <Alert
            type="info"
            message={(
              <span style={{ fontSize: 12 }}>
                This bill leaves your queue and appears in theirs. The supplier keeps its owner, so
                its next bill comes back to whoever owns it. The hand-off is recorded.
              </span>
            )}
          />
        </Space>
      )}
    </Modal>
  );

  return (
    <PageLayout>
      <div className="page-header">
        <h1>Opex Control</h1>
        <p>Opex budgets, supplier ownership and bill approval across the group</p>
      </div>

      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} closable onClose={() => setError(null)} />}

      {noEntities ? (
        <Alert
          type="warning" showIcon
          message="You have no entity budget access yet"
          description="This page is granted per entity. Ask an executive or the finance manager to grant you access to the entities you are responsible for."
        />
      ) : (
        <Tabs
          activeKey={tab}
          onChange={setTab}
          items={[
            { key: 'mine', label: 'My day', children: myDay },
            { key: 'suppliers', label: 'My suppliers', children: mySuppliersTab },
            {
              key: 'entity',
              label: 'Entity budgets',
              children: (
                <>
                  {entityHeader}
                  <Spin spinning={loading}>{entityTabs}</Spin>
                </>
              ),
            },
            { key: 'report', label: 'Report', children: reportTab },
            ...(access?.canManageAccess ? [{
              key: 'access',
              label: 'People & access',
              children: (
                <>
                  {rulesPanel}
                  {unassignedPanel}
                  {peopleTab}
                </>
              ),
            }] : []),
          ]}
        />
      )}

      {/* The drawer the expected-cost entry was specified as: amount, entity,
          month or frequency, account. */}
      {newBudgetDrawer}
      {mySupplierDrawer}
      {myLineDrawer}
      {supplierDrawer}
      {handOnModal}
      {approveModal}
      {editDrawer}

      <Drawer
        title="Expected cost"
        width={430}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        extra={(
          <Space>
            <Button onClick={() => setDrawerOpen(false)}>Cancel</Button>
            <Button type="primary" loading={saving} onClick={submitDrawer}
              style={{ background: '#54A2CC', borderColor: '#54A2CC' }}
            >
              Save
            </Button>
          </Space>
        )}
      >
        <Form
          form={form} layout="vertical"
          initialValues={{ dimension: 'supplier', frequency: 'one_off', months: 1, month }}
        >
          <Form.Item label="Entity">
            <Input value={current?.name} disabled />
          </Form.Item>
          <Form.Item
            name="dimension"
            label="Against"
            extra={(
              <span style={{ fontSize: 11 }}>
                On an account budgeted as a monthly envelope, the account line <i>is</i> the
                commitment. On a per-supplier account it caps the category below the sum of its
                supplier lines, and does not add to the total.
              </span>
            )}
          >
            <Select options={[
              { value: 'supplier', label: 'A supplier' },
              { value: 'account', label: 'An expense account — envelope or cap' },
            ]}
            />
          </Form.Item>
          <Form.Item shouldUpdate noStyle>
            {({ getFieldValue }) => (getFieldValue('dimension') === 'supplier' ? (
              <Form.Item name="supplierId" label="Supplier" rules={[{ required: true, message: 'Choose a supplier' }]}>
                <Select
                  showSearch optionFilterProp="label"
                  placeholder="Search suppliers"
                  options={suppliers.map((s) => ({ value: s.id, label: s.name }))}
                  notFoundContent="No suppliers recorded for this entity yet"
                />
              </Form.Item>
            ) : null)}
          </Form.Item>
          <Form.Item name="amount" label="Amount (AUD)" rules={[{ required: true, message: 'Enter an amount' }]}>
            <InputNumber
              style={{ width: '100%' }} min={0} step={100}
              formatter={(v) => `$ ${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={(v) => v.replace(/\$\s?|(,*)/g, '')}
            />
          </Form.Item>
          <Form.Item name="month" label="First month" rules={[{ required: true }]}>
            <DatePicker picker="month" style={{ width: '100%' }} allowClear={false} />
          </Form.Item>
          <Form.Item name="frequency" label="Frequency">
            <Select options={[
              { value: 'one_off', label: 'One-off — that month only' },
              { value: 'monthly', label: 'Every month' },
              { value: 'quarterly', label: 'Every quarter' },
              { value: 'annual', label: 'Once a year' },
            ]}
            />
          </Form.Item>
          <Form.Item shouldUpdate noStyle>
            {({ getFieldValue }) => (getFieldValue('frequency') !== 'one_off' ? (
              <Form.Item
                name="months" label="How many occurrences"
                extra="Expanded into one line per month on save, so a single month can be adjusted later without breaking the series. Twelve is the planning horizon."
              >
                <InputNumber min={1} max={12} style={{ width: '100%' }} />
              </Form.Item>
            ) : null)}
          </Form.Item>
          {/* The picker sets both fields, so shouldUpdate is what makes the
              Select redraw with the choice — a Form.Item name= would have its
              injected value overridden by the explicit one and never update. */}
          <Form.Item shouldUpdate noStyle>
            {() => (
              <Form.Item label="Expense account">
                {accountPicker(
                  entity,
                  form.getFieldValue('accountCode'),
                  (code, name) => form.setFieldsValue({ accountCode: code, accountName: name }),
                )}
              </Form.Item>
            )}
          </Form.Item>
          {/* The name is carried with the code rather than typed, so the two
              cannot disagree. */}
          <Form.Item name="accountCode" hidden><Input /></Form.Item>
          <Form.Item name="accountName" hidden><Input /></Form.Item>

          <Form.Item name="note" label="Note">
            <Input.TextArea rows={2} maxLength={500} showCount />
          </Form.Item>
        </Form>
      </Drawer>
    </PageLayout>
  );
}
